import { jsPDF } from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { numberFormat } from "../../../../utils/NumberFormat";
import { PdfUtils } from "../../../../utils/pdf/PdfUtils";
import { getConfig } from "../../../../config/config";
import defaultHeaderImg from "../../../../assets/form/97_header_quotation.png";
import footerImg from "../../../../assets/form/97_footer_quotation.png";

// Allow selecting header image dynamically by filename/code (LookupCode).
// Bundles all images in assets/form so we can resolve by string at runtime.
// eslint-disable-next-line no-undef
const formImages = require.context("../../../../assets/form", false, /\.(png|jpe?g)$/i);

const tryLoadFormImage = (fileName) => {
  if (!fileName) return null;
  try {
    const mod = formImages(`./${fileName}`);
    return mod?.default ?? mod;
  } catch (_) {
    return null;
  }
};

const normalizeUrlBase = (baseUrl) => {
  const s = String(baseUrl || "").trim();
  if (!s) return "";
  return s.endsWith("/") ? s : `${s}/`;
};

const trimSlashes = (s) => String(s || "").replace(/^\/+|\/+$/g, "");

const preferHttpsIfPageIsHttps = (url) => {
  const s = String(url || "").trim();
  if (!s) return "";
  try {
    if (typeof window !== "undefined" && window?.location?.protocol === "https:" && s.startsWith("http://")) {
      return `https://${s.slice("http://".length)}`;
    }
  } catch (_) { }
  return s;
};

const rewriteToDevProxyIfLocalhost = (url) => {
  const s = String(url || "").trim();
  if (!s) return "";
  try {
    if (typeof window === "undefined") return s;
    const host = String(window?.location?.hostname || "").trim().toLowerCase();
    const isLocalhost = host === "localhost" || host === "127.0.0.1";
    if (!isLocalhost) return s;

    const u = new URL(s, window.location.origin);
    if (u.hostname === "online.ismartsales.com" && u.pathname.startsWith("/web/form/")) {
      // Served by CRA dev proxy from src/setupProxy.js
      return `${u.pathname}${u.search || ""}`;
    }
  } catch (_) {
    // ignore
  }
  return s;
};

const joinUrl = (baseUrl, ...parts) => {
  const base = normalizeUrlBase(baseUrl);
  if (!base) return "";

  const p = parts
    .map((v) => trimSlashes(v))
    .filter(Boolean)
    .join("/");

  return p ? `${base}${p}` : base;
};

const inferImageType = (fileNameOrUrl, fallback = "PNG") => {
  const s = String(fileNameOrUrl || "").toLowerCase();
  if (s.endsWith(".jpg") || s.endsWith(".jpeg")) return "JPEG";
  if (s.endsWith(".png")) return "PNG";
  return fallback;
};

const getDocumentBaseUrl = () => {
  const fromEnv =
    (typeof process !== "undefined" && process.env && (process.env.REACT_APP_PHOTO_DOCUMENT || process.env.PHOTO_DOCUMENT))
      ? String(process.env.REACT_APP_PHOTO_DOCUMENT || process.env.PHOTO_DOCUMENT).trim()
      : "";
  const fromConfig = String(getConfig?.("PHOTO_DOCUMENT", "") || "").trim();
  const fallbackEnv =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_FILE_URL)
      ? String(process.env.REACT_APP_FILE_URL).trim()
      : "";
  const fallbackConfig = String(getConfig?.("FILE_URL", "") || "").trim();

  return fromEnv || fromConfig || fallbackEnv || fallbackConfig || "";
};

const baseEndsWithLocation = (baseUrl, location) => {
  const base = String(baseUrl || "").trim().replace(/\/+$/, "");
  const loc = String(location || "").trim().replace(/^\/+|\/+$/g, "");
  if (!base || !loc) return false;
  return base.toLowerCase().endsWith(`/${loc.toLowerCase()}`);
};

const buildFileImageUrlFromEnv = ({ fileLocation, fileName, defaultLocation = "" } = {}) => {
  const base = normalizeUrlBase(getDocumentBaseUrl());
  if (!base || !fileName) return "";
  const loc = String(fileLocation || defaultLocation || "").trim();
  // Avoid duplicate folder when base already includes it (common: PHOTO_DOCUMENT ends with '/form').
  const effectiveLoc = baseEndsWithLocation(base, loc || defaultLocation) ? "" : (loc || defaultLocation);
  const full = joinUrl(base, effectiveLoc, fileName);
  return rewriteToDevProxyIfLocalhost(preferHttpsIfPageIsHttps(full));
};

const withPdfCacheBust = (url) => {
  const s = String(url || "").trim();
  if (!s) return "";
  if (s.startsWith("data:image/")) return s;
  try {
    if (typeof window === "undefined") return s;
    const u = new URL(s, window.location.origin);
    u.searchParams.set("__pdfcb", String(Date.now()));
    if (s.startsWith("/")) return `${u.pathname}${u.search}`;
    return u.toString();
  } catch (_) {
    return s;
  }
};

const tryLoadAsImageElement = (src) =>
  new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = withPdfCacheBust(src);
    } catch (e) {
      reject(e);
    }
  });

const resolveImageForJsPdf = async ({ src, type }) => {
  if (!src) return null;

  // data URL is already compatible
  if (typeof src === "string" && src.startsWith("data:image/")) {
    return { source: src, type: type || inferImageType(src) };
  }

  // For any string URL (webpack asset URL or remote URL), prefer Image() to avoid fetch/CORS.
  if (typeof src === "string") {
    try {
      const img = await tryLoadAsImageElement(src);
      return { source: img, type: type || inferImageType(src) };
    } catch (_) {
      // ignore and try base64
    }

    try {
      const dataUrl = await PdfUtils.getBase64ImageFromUrl(withPdfCacheBust(src));
      if (typeof dataUrl === "string" && dataUrl.startsWith("data:image/")) {
        return { source: dataUrl, type: type || inferImageType(src) };
      }
    } catch (_) {
      // ignore
    }
  }

  // Non-string sources (already HTMLImageElement/canvas/etc)
  return { source: src, type: type || "PNG" };
};

// THSarabunNew has no glyph for C0/C1 control or zero-width characters, and jsPDF's Identity-H
// encoder (pdfEscape16) *returns early* on the first glyph it cannot map — silently
// dropping the rest of that text run. A single stray control char in the data (e.g.
// pasted from a mobile app into Contact.Name or ItemDescription) therefore truncates
// the whole remainder of the line in the PDF. Strip them before anything is drawn.
// \n is kept because jsPDF uses it for line breaks; \r and \t are folded to safe
// equivalents instead of being removed outright so wording/spacing stays intact.
// eslint-disable-next-line no-control-regex
const PDF_UNSAFE_CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B\u200C\u200D\uFEFF]/g;

const sanitizePdfText = (value) => {
  const s = String(value);
  if (s.indexOf("\r") === -1 && s.indexOf("\t") === -1 && !PDF_UNSAFE_CONTROL_RE.test(s)) {
    // Fast path: nothing to clean (regex has /g, so reset lastIndex before returning).
    PDF_UNSAFE_CONTROL_RE.lastIndex = 0;
    return s;
  }
  PDF_UNSAFE_CONTROL_RE.lastIndex = 0;

  return s
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\t/g, " ")
    .replace(PDF_UNSAFE_CONTROL_RE, "");
};

const sanitizePdfData = (value, depth = 0) => {
  if (typeof value === "string") return sanitizePdfText(value);
  if (value === null || typeof value !== "object") return value;

  // Guard against unexpectedly deep/recursive payloads.
  if (depth > 12) return value;
  if (value instanceof Date) return value;

  if (Array.isArray(value)) return value.map((v) => sanitizePdfData(v, depth + 1));

  const out = {};
  for (const key of Object.keys(value)) {
    out[key] = sanitizePdfData(value[key], depth + 1);
  }
  return out;
};

export class QuotationPdfGenerator {
  constructor(data = {}, options = {}) {
    this.data = sanitizePdfData(data);
    this.options = options;
    this.pdf = null;
    this.yAxis = 0;
    // Must match the family name registered in PdfInitiator (jsPDF.addFont)
    this.fontName = "THSarabunNew";

    // Keep enough leading for Thai stacking marks while preserving page layout.
    this.lineHeightFactor = 1.25;

    // Slightly larger for readability; most layouts adapt to content height.
    this.baseFontSize = 12;

    this.showInfoSection = options.showInfoSection ?? true;
    this.summaryOnEveryPage = options.summaryOnEveryPage ?? false;
    this.rowsPerPage = 10; // คุมความสูงให้เหลือที่สำหรับ summary + signature + footer
    this.summaryWidth = 70;

    this.pdfOption = {
      orientation: "p",
      format: "a4",
      unit: "mm",
      lineHeight: this.lineHeightFactor,
    };

    this.pdfConfig = {
      margin: { t: 10, b: 10, l: 10, r: 10 },
    };

    // Track bottom Y of footer-remarks (ExtraConditions + RemarkFooter) per page
    // so we can place footer.png snugly on the final page.
    this.pageFooterRemarksBottomY = Object.create(null);

    // Explicit anchor for the *latest* footer-remarks bottom, used to place footer.png reliably.
    this.lastFooterRemarksPage = null;
    this.lastFooterRemarksBottomY = null;

    this._resolvedFooterImage = null;
    this._resolvedHeaderImage = null;
    this._resolvedSalesmanSignatureImage = undefined;
  }

  getCompanyCode() {
    return String(this.data?.Company ?? this.data?.QuotationTransaction?.Company ?? "").trim();
  }

  isStrictCompanyHeaderFooterEnabled() {
    return !!this.options?.strictCompanyHeaderFooter;
  }

  hasDynamicHeaderSelection() {
    const code = String(this.options?.docHeaderTypeCode ?? "").trim();
    const filename = String(this.options?.docHeaderImageFilename ?? "").trim();
    const location = String(this.options?.docHeaderImageFileLocation ?? "").trim();
    return !!(code || filename || location);
  }

  allowMissingCompanyHeaderFooter() {
    return !!this.options?.allowMissingCompanyHeaderFooter;
  }

  getCompanyHeaderFooterLocation() {
    // Default to "" because PHOTO_DOCUMENT may already include the correct folder
    const loc = String(this.options?.companyHeaderFooterLocation || "").trim();
    return loc || "";
  }

  getSalesmanCode() {
    try {
      const qtTransactionRaw = this.data?.QuotationTransaction;
      const qtTransaction = Array.isArray(qtTransactionRaw) ? qtTransactionRaw[0] : qtTransactionRaw;
      const code =
        qtTransaction?.SalesmanCode ??
        this.data?.SalesmanCode ??
        this.data?.QuotationTransaction?.SalesmanCode ??
        "";
      return String(code || "").trim();
    } catch (_) {
      return "";
    }
  }

  getSalesmanSignatureLocation() {
    // Use the same location as footer/header images (per requirement).
    if (this.isStrictCompanyHeaderFooterEnabled()) {
      return this.getCompanyHeaderFooterLocation();
    }
    const loc = String(this.options?.docFooterImageFileLocation || "").trim();
    return loc || "form";
  }

  isSalesmanSignatureEnabled() {
    // Always try to resolve signature first on every PDF.
    // If not found, renderer will safely skip drawing (no PDF failure).
    return this.options?.enableSalesmanSignature !== false;
  }

  async resolveSalesmanSignatureImageSource() {
    if (this._resolvedSalesmanSignatureImage !== undefined) return this._resolvedSalesmanSignatureImage;

    try {
      if (!this.isSalesmanSignatureEnabled()) {
        this._resolvedSalesmanSignatureImage = null;
        return this._resolvedSalesmanSignatureImage;
      }

      const company = this.getCompanyCode();
      const salesmanCodeRaw = this.getSalesmanCode();
      const salesmanCode = String(salesmanCodeRaw || "").trim();
      if (!company || !salesmanCode) {
        this._resolvedSalesmanSignatureImage = null;
        return this._resolvedSalesmanSignatureImage;
      }

      // Prevent path traversal / accidental folders.
      const safeToken = (s) => String(s || "").trim().replace(/[\\/]/g, "");
      const companySafe = safeToken(company);
      const salesmanSafe = safeToken(salesmanCode);

      const fileLocation = this.getSalesmanSignatureLocation();
      const defaultLocation = this.isStrictCompanyHeaderFooterEnabled() ? "" : "form";

      const buildCandidates = () => {
        // Keep probes short to avoid long 404 chains in dev while still covering common cases.
        const out = [];
        if (/\.(png|jpe?g)$/i.test(salesmanSafe)) {
          out.push(`${companySafe}_${salesmanSafe}`, salesmanSafe);
        } else {
          out.push(
            `${companySafe}_${salesmanSafe}.png`,
            `${companySafe}_${salesmanSafe}.PNG`,
            `${salesmanSafe}.png`,
            `${salesmanSafe}.PNG`
          );
        }
        return out.filter((v, i) => v && out.indexOf(v) === i);
      };

      const fileNames = buildCandidates();
      const candidates = [];
      for (const fileName of fileNames) {
        const envUrl = buildFileImageUrlFromEnv({
          fileLocation,
          fileName,
          defaultLocation,
        });
        if (envUrl) candidates.push({ src: envUrl, type: inferImageType(fileName) });

        const localSrc = tryLoadFormImage(fileName);
        if (localSrc) candidates.push({ src: localSrc, type: inferImageType(fileName) });
      }

      for (const c of candidates) {
        // Signature is optional; silently skip when missing.
        try {
          if (typeof c?.src === "string") {
            if (c.src.startsWith("data:image/")) {
              this._resolvedSalesmanSignatureImage = { source: c.src, type: c.type || inferImageType(c.src) };
              return this._resolvedSalesmanSignatureImage;
            }

            const img = await tryLoadAsImageElement(c.src);
            if (img) {
              this._resolvedSalesmanSignatureImage = { source: img, type: c.type || inferImageType(c.src) };
              return this._resolvedSalesmanSignatureImage;
            }
          } else {
            const resolved = await resolveImageForJsPdf(c);
            if (resolved?.source) {
              this._resolvedSalesmanSignatureImage = { source: resolved.source, type: resolved.type || "PNG" };
              return this._resolvedSalesmanSignatureImage;
            }
          }
        } catch (_) {
          // ignore
        }
      }

      this._resolvedSalesmanSignatureImage = null;
      return this._resolvedSalesmanSignatureImage;
    } catch (_) {
      // Never block PDF generation because of signature resolution errors.
      this._resolvedSalesmanSignatureImage = null;
      return this._resolvedSalesmanSignatureImage;
    }
  }

  async tryRenderSalesmanSignatureOnFooter({ footerX, footerY, footerW, footerH } = {}) {
    try {
      const sig = await this.resolveSalesmanSignatureImageSource();
      if (!sig?.source) return;

      const pdf = this.pdf;
      const getImageSize = (src) => {
        try {
          if (typeof Image !== "undefined" && src instanceof Image) {
            const w = Number(src.naturalWidth || src.width);
            const h = Number(src.naturalHeight || src.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
          }
        } catch (_) { }

        try {
          if (typeof pdf?.getImageProperties === "function") {
            const p = pdf.getImageProperties(src);
            const w = Number(p?.width);
            const h = Number(p?.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
          }
        } catch (_) { }

        return null;
      };

      const fx = Number(footerX);
      const fy = Number(footerY);
      const fw = Number(footerW);
      const fh = Number(footerH);
      if (!Number.isFinite(fx) || !Number.isFinite(fy) || !Number.isFinite(fw) || !Number.isFinite(fh)) return;

      // Move signature a bit to the right and make it larger.
      const padX = 17;
      const padY = 2;
      const liftY = 6;
      const maxW = Math.max(28, Math.min(102, fw * 0.57));
      const maxH = Math.max(15, Math.min(36, fh * 1.08));

      const size = getImageSize(sig.source);
      let drawW = maxW;
      let drawH = maxH;
      if (size) {
        const ratio = size.w / size.h;
        if (Number.isFinite(ratio) && ratio > 0) {
          drawW = maxW;
          drawH = Math.max(1, maxW / ratio);
          if (drawH > maxH) {
            drawH = maxH;
            drawW = Math.max(1, maxH * ratio);
          }
        }
      }

      const x = fx + padX;
      const baseY = Math.max(padY, (fh - drawH) / 2);
      const y = fy + (baseY - liftY);
      pdf.addImage(sig.source, sig.type || "PNG", x, y, drawW, drawH);
    } catch (_) {
      // ignore - missing signature should not break PDF generation
    }
  }

  async resolveHeaderImageSourceStrict() {
    if (this._resolvedHeaderImage) return this._resolvedHeaderImage;
    const company = this.getCompanyCode();
    if (!company) throw new Error("MISSING_COMPANY_CODE_FOR_PDF");

    const fileName = `${company}_header_quotation.png`;
    const url = buildFileImageUrlFromEnv({
      fileLocation: this.getCompanyHeaderFooterLocation(),
      fileName,
      defaultLocation: "",
    });
    if (!url) throw new Error("MISSING_PHOTO_DOCUMENT_BASE_URL_FOR_PDF");

    const resolved = await resolveImageForJsPdf({ src: url, type: "PNG" });
    if (!resolved?.source) throw new Error("MISSING_PDF_HEADER_IMAGE");

    this._resolvedHeaderImage = { source: resolved.source, type: resolved.type || "PNG", url, fileName };
    return this._resolvedHeaderImage;
  }

  async resolveFooterImageSourceStrict() {
    if (this._resolvedFooterImage) return this._resolvedFooterImage;
    const company = this.getCompanyCode();
    if (!company) throw new Error("MISSING_COMPANY_CODE_FOR_PDF");

    const fileName = `${company}_footer_quotation.png`;
    const url = buildFileImageUrlFromEnv({
      fileLocation: this.getCompanyHeaderFooterLocation(),
      fileName,
      defaultLocation: "",
    });
    if (!url) throw new Error("MISSING_PHOTO_DOCUMENT_BASE_URL_FOR_PDF");

    const resolved = await resolveImageForJsPdf({ src: url, type: "PNG" });
    if (!resolved?.source) throw new Error("MISSING_PDF_FOOTER_IMAGE");

    this._resolvedFooterImage = { source: resolved.source, type: resolved.type || "PNG", url, fileName };
    return this._resolvedFooterImage;
  }

  getRemarkFooterHeaderRow() {
    return {
      subject: "เงื่อนไขการชำระเงิน :",
      remarks: "",
      // Render subject only + underline (handled in renderItemFooterRemarksTable)
      style: "U",
      color: "",
    };
  }

  isRemarkFooterHeaderRow(row) {
    const header = this.getRemarkFooterHeaderRow();
    const normalize = (s) => String(s ?? "").replace(/\s+/g, "").trim();
    const a = normalize(row?.subject);
    const b = normalize(header?.subject);
    return !!a && !!b && a === b;
  }

  async resolveFooterImageSource() {
    if (this._resolvedFooterImage) return this._resolvedFooterImage;

    if (this.isStrictCompanyHeaderFooterEnabled()) {
      return this.resolveFooterImageSourceStrict();
    }

    const company = String(
      this.data?.Company ?? this.data?.QuotationTransaction?.Company ?? ""
    ).trim();

    const footerLocation = String(this.options?.docFooterImageFileLocation || "").trim() || "form";
    const footerFileNameFromOptions = String(this.options?.docFooterImageFilename || "").trim();

    const buildFooterCandidates = () => {
      const out = [];

      // Highest priority: filename explicitly provided by caller/API mapping
      if (footerFileNameFromOptions) out.push(footerFileNameFromOptions);

      // Prefer company-specific footer file naming
      if (company) {
        out.push(
          `${company}_footer_quotation.png`,
          `${company}_footer_quotation.jpg`,
          `${company}_footer_quotation.jpeg`
        );
      }

      // Generic names (in case file store doesn't prefix with company)
      out.push("footer_quotation.png", "footer_quotation.jpg", "footer_quotation.jpeg");

      // Legacy fallback (keep as last-resort)
      out.push("97_footer_quotation.png");
      return out;
    };

    const fileNames = buildFooterCandidates();

    const candidates = [];
    for (const fileName of fileNames) {
      const envUrl = buildFileImageUrlFromEnv({
        fileLocation: footerLocation,
        fileName,
        defaultLocation: "form",
      });
      if (envUrl) candidates.push({ src: envUrl, type: inferImageType(fileName) });

      const localSrc = tryLoadFormImage(fileName);
      if (localSrc) candidates.push({ src: localSrc, type: inferImageType(fileName) });
    }

    // Also keep current bundled import as final fallback (it points to 97_footer_quotation.png)
    if (footerImg) candidates.push({ src: footerImg, type: "PNG" });

    for (const c of candidates) {
      const resolved = await resolveImageForJsPdf(c);
      if (resolved?.source) {
        this._resolvedFooterImage = { source: resolved.source, type: resolved.type || "PNG" };
        return this._resolvedFooterImage;
      }
    }

    throw new Error("Unable to resolve footer image source");
  }

  getCurrentPageNumber(pdf = this.pdf) {
    try {
      // jsPDF APIs differ by version; try multiple ways.
      const info1 = typeof pdf?.getCurrentPageInfo === "function" ? pdf.getCurrentPageInfo() : null;
      const n1 = Number(info1?.pageNumber);
      if (Number.isFinite(n1)) return n1;

      const info2 = pdf?.internal?.getCurrentPageInfo?.();
      const n2 = Number(info2?.pageNumber);
      if (Number.isFinite(n2)) return n2;

      // Fallback: during generation we always draw on the latest (last) page.
      const n3 = typeof pdf?.getNumberOfPages === "function" ? Number(pdf.getNumberOfPages()) : NaN;
      return Number.isFinite(n3) && n3 > 0 ? n3 : null;
    } catch (_) {
      try {
        const n = typeof pdf?.getNumberOfPages === "function" ? Number(pdf.getNumberOfPages()) : NaN;
        return Number.isFinite(n) && n > 0 ? n : null;
      } catch (__) {
        return null;
      }
    }
  }

  markFooterRemarksBottomY(y, pdf = this.pdf) {
    const page = this.getCurrentPageNumber(pdf);
    if (!Number.isFinite(y) || !page) return;
    const prev = Number(this.pageFooterRemarksBottomY[page]);
    this.pageFooterRemarksBottomY[page] = Number.isFinite(prev) ? Math.max(prev, y) : y;

    // Keep a stable reference to the latest page anchor.
    const prevPage = Number(this.lastFooterRemarksPage);
    const prevY = Number(this.lastFooterRemarksBottomY);
    if (!Number.isFinite(prevPage) || page > prevPage) {
      this.lastFooterRemarksPage = page;
      this.lastFooterRemarksBottomY = y;
    } else if (page === prevPage) {
      this.lastFooterRemarksBottomY = Number.isFinite(prevY) ? Math.max(prevY, y) : y;
    }
  }

  getFooterImageHeight() {
    // NOTE: The current footer asset (e.g. 97_footer_quotation.png ~ 2480x412)
    // needs ~33mm height when rendered full table width (~198mm) without distortion.
    // Keep a small buffer so the image can stay full-width and not get squashed.
    return 40;
  }

  getFooterImageTopGap() {
    // Match the common vertical gap used between major sections (e.g. after info box).
    return 5;
  }

  getReservedBottom({ includeFooterImage = false } = {}) {
    const b = this.pdfConfig.margin?.b || 10;
    if (!includeFooterImage) {
      // Small breathing room for page number / bottom padding.
      return b + 2;
    }
    // Reserve footer image height + extra spacing above it.
    return b + this.getFooterImageHeight() + this.getFooterImageTopGap() + 6;
  }

  resolveFontStyle(style = "normal", pdf = this.pdf) {
    const safeStyle = style || "normal";
    const doc = pdf || this.pdf;
    if (!doc || typeof doc.getFontList !== "function") return safeStyle;

    const list = doc.getFontList() || {};
    const styles = list?.[this.fontName];

    // If the font family isn't registered yet, keep the requested style.
    // (jsPDF will fall back internally; callers should ensure PdfInitiator ran.)
    if (!Array.isArray(styles) || styles.length === 0) return safeStyle;

    // IMPORTANT: If style variant isn't registered, forcing "normal" prevents jsPDF
    // from switching to a different default font that can't render Thai.
    if (styles.includes(safeStyle)) return safeStyle;
    return styles.includes("normal") ? "normal" : styles[0];
  }

  getItemTableColumnWidths() {
    // IMPORTANT: Do not shrink these widths; use them as the single source of truth.
    // Must match `columnStyles` in renderItemTable.
    return [12, 28, 59, 18, 27, 27, 27];
  }

  getFormBox() {
    const widths = this.getItemTableColumnWidths();
    const formW = widths.reduce((sum, v) => sum + (Number(v) || 0), 0);
    const pageW = Number(this.width || 0);
    const x = Math.max(0, (pageW - formW) / 2);
    const midX = x + formW / 2;
    return { x, w: formW, midX };
  }

  setFont(sizeOffset = 0, style = "normal") {
    const resolvedStyle = this.resolveFontStyle(style, this.pdf);
    this.pdf.setFont(this.fontName, resolvedStyle);
    this.pdf.setFontSize(this.baseFontSize + sizeOffset);
  }

  getLabel(key, fallback) {
    const { translate, translator } = this.options;
    if (!translator || !translate) return fallback;
    return translator(translate[key], fallback);
  }

  renderLabelValueBlock({
    pdf,
    label,
    value,
    x,
    y,
    maxWidth,
    lineHeight = null,
  }) {
    this.setFont(0, "bold");
    const labelWidth = pdf.getTextDimensions(label).w;
    pdf.text(label, x, y);

    this.setFont();
    const valueX = x + labelWidth + 2;
    const valueMaxWidth = maxWidth - labelWidth - 2;
    const splitVal = pdf.splitTextToSize(value || "-", valueMaxWidth);

    pdf.text(splitVal, valueX, y, { lineHeightFactor: this.lineHeightFactor });

    const effectiveLineHeight = (() => {
      if (Number.isFinite(lineHeight) && lineHeight > 0) return lineHeight;
      const ptToMm = 0.3527777778;
      const fontSizePt = typeof pdf.getFontSize === "function" ? pdf.getFontSize() : this.baseFontSize;
      return fontSizePt * ptToMm * this.lineHeightFactor;
    })();

    return y + splitVal.length * effectiveLineHeight;
  }

  getMaxContentY({ includeFooterImage = false } = {}) {
    return this.height - this.getReservedBottom({ includeFooterImage });
  }

  getSummaryAndRemarkHeight() {
    const rowH = 6.5;
    const summaryRows = 3;
    return summaryRows * rowH;
  }

  getItemTableHead() {
    return [
      ["ลำดับ", "รหัสสินค้า", "ชื่อสินค้า", "จำนวน", "หน่วยละ", "ส่วนลด", "จำนวนเงิน"],
      ["No.", "ItemCode", "ItemDescription", "Quantity", "Unit Price", "Discount", "Amount"],
    ];
  }

  buildItemTableRows(items = [], startIndex = 0) {
    const tableRows = [];

    const hasSecondUnitInfo = (item) => {
      if (!item || typeof item !== "object") return false;
      const u2 = String(item.Unit2 ?? item.unit2 ?? "").trim();
      const q2raw = item.Quantity2 ?? item.quantity2;
      const q2 = q2raw === null || q2raw === undefined ? "" : String(q2raw).trim();

      // Treat Quantity2=0 as "not provided" (many APIs send defaults).
      const q2n = Number(q2);
      const hasPositiveQ2 = Number.isFinite(q2n) ? q2n > 0 : !!q2;
      return !!(u2 && hasPositiveQ2);
    };

    const formatQtyNoDecimal = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return "0";
      // Round to whole number and force 0 fraction digits.
      return new Intl.NumberFormat("en", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(n));
    };

    const normalizeRemarkText = (raw) => {
      const txt = String(raw || "").trim();
      if (!txt) return "";
      return txt
        // Normalize all CR/LF combinations into LF so jsPDF can split/render reliably.
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        // Some backends may send a literal "\\n" sequence; convert it to a real newline.
        .replace(/\\n/g, "\n")
        .replace(/^\n+/, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    };

    // Loose key for de-duplication across remark sources (item remark vs product remark)
    // We intentionally remove whitespace + punctuation/symbols so that the same remark
    // with different formatting does not get duplicated.
    const remarkCompareKey = (raw) =>
      normalizeRemarkText(raw)
        .toLowerCase()
        // Keep only Thai/Latin letters and digits.
        .replace(/[^a-z0-9\u0E00-\u0E7F]+/gi, "");

    items.forEach((item, index) => {
      const productCode = item.ProductCode || "";
      const productName = item.ProductName || "";
      const rawItemRemark = String(item.ItemRemarkNew ?? item.ItemRemark ?? "").trim();

      const itemRemark = normalizeRemarkText(rawItemRemark);

      const itemRemarkSource = item?.ItemRemarkNew
        ? "ItemRemarkNew"
        : item?.ItemRemark
          ? "ItemRemark"
          : "";

      const productRemarks = Array.isArray(item.ProductRemarks) && item.ProductRemarks.length
        ? item.ProductRemarks
        : Array.isArray(this.data.ProductRemarks)
          ? this.data.ProductRemarks.filter((r) =>
            String(r?.ProductCode || "").trim() === String(productCode || "").trim()
          )
          : [];

      const productRemarksSource = Array.isArray(item.ProductRemarks) && item.ProductRemarks.length
        ? "item.ProductRemarks"
        : Array.isArray(this.data.ProductRemarks)
          ? "data.ProductRemarks"
          : "";

      let descCell = `${productName}`;

      // Collect remark blocks from multiple sources but avoid duplicates.
      // Some APIs already put product remark into ItemRemark, while also returning ProductRemarks.
      const remarkBlocks = [];
      const pushRemarkBlock = (block) => {
        const text = normalizeRemarkText(block);
        if (!text) return;
        const key = remarkCompareKey(text);
        if (!key) {
          remarkBlocks.push(text);
          return;
        }

        for (let i = 0; i < remarkBlocks.length; i += 1) {
          const existing = remarkBlocks[i];
          const existingKey = remarkCompareKey(existing);
          if (!existingKey) continue;

          // If one contains the other, keep only the larger/superset text.
          if (key.includes(existingKey)) {
            remarkBlocks[i] = text;
            return;
          }
          if (existingKey.includes(key)) {
            return;
          }
        }

        remarkBlocks.push(text);
      };

      let productRemarksLinesUsed = [];
      let productRemarksTextUsed = "";

      if (productRemarks.length > 0) {
        const sorted = [...productRemarks].sort((a, b) => (a.LineNo || 0) - (b.LineNo || 0));
        const seenRemarkKeys = new Set();
        const lines = [];
        sorted.forEach((r) => {
          const t = normalizeRemarkText(r?.RemarksNew ?? r?.Remarks);
          if (!t) return;
          const k = remarkCompareKey(t);
          if (k && seenRemarkKeys.has(k)) return;
          if (k) seenRemarkKeys.add(k);
          lines.push(t);
        });
        const remarksText = lines.join("\n").trim();
        productRemarksLinesUsed = lines.slice();
        productRemarksTextUsed = remarksText;
      }

      const buildExtraItemRemark = ({ itemText, productText }) => {
        const itemT = normalizeRemarkText(itemText);
        if (!itemT) return "";
        const prodT = normalizeRemarkText(productText);
        const prodKey = remarkCompareKey(prodT);
        if (!prodT || !prodKey) return itemT;

        // Normalize item bullets to lines, but avoid breaking numeric ranges like 23.5-25
        const normalized = itemT
          .replace(/\t/g, " ")
          // Add newline before bullet hyphen when it looks like a bullet (start/whitespace/newline)
          .replace(/(^|[\n\r \f\v])-[ \t]*/g, "\n- ")
          .replace(/^\n+/, "")
          .replace(/\n{3,}/g, "\n\n");

        const lines = normalized
          .split("\n")
          .map((s) => String(s ?? "").trim())
          .filter(Boolean);

        const kept = [];
        let droppedCount = 0;
        for (const ln of lines) {
          const content = ln.startsWith("-") ? ln.replace(/^-[ \t]*/, "") : ln;
          const k = remarkCompareKey(content);
          if (k && prodKey.includes(k)) {
            // This line already exists in ProductRemarks; drop it to prevent duplication.
            droppedCount += 1;
            continue;
          }
          kept.push(ln.startsWith("-") ? `- ${content}`.trim() : content);
        }

        return { text: kept.join("\n").trim(), keptCount: kept.length, droppedCount, totalCount: lines.length };
      };

      // Build remark blocks:
      // - Prefer ProductRemarks as the main spec block (from master table)
      // - Keep only the extra part from ItemRemark (if any)
      if (productRemarksTextUsed) {
        pushRemarkBlock(productRemarksTextUsed);
        const extra = buildExtraItemRemark({
          itemText: itemRemark,
          productText: productRemarksTextUsed,
        });
        const extraItemRemark = typeof extra === "object" ? String(extra?.text ?? "").trim() : String(extra ?? "").trim();
        pushRemarkBlock(extraItemRemark);

        // Store for debug log below.
        item.__pdfExtraItemRemarkDebug = typeof extra === "object" ? extra : null;
      } else {
        pushRemarkBlock(itemRemark);
      }

      // Debug: show where item remark content comes from and what ended up in the description.
      // Log only when BOTH sources exist (common cause of duplicated remark blocks).
      try {
        const hasItemRemark = !!itemRemark;
        const hasProductRemark = productRemarks.length > 0 && !!productRemarksTextUsed;
        if (hasItemRemark && hasProductRemark) {
          if (!this.__itemRemarkSourceDebugSeen) this.__itemRemarkSourceDebugSeen = new Set();
          const debugKey = `${String(this.data?.DocNumber ?? "")}::${String(productCode)}::${startIndex + index + 1}`;
          if (!this.__itemRemarkSourceDebugSeen.has(debugKey)) {
            this.__itemRemarkSourceDebugSeen.add(debugKey);
            // eslint-disable-next-line no-console
            console.log("[QuotationPdf] ItemRemark sources", {
              docNumber: this.data?.DocNumber,
              rowNo: startIndex + index + 1,
              itemNumber: item?.ItemNumber,
              productCode,
              itemRemarkSource,
              itemRemarkPreview: String(itemRemark).slice(0, 220),
              productRemarksSource,
              productRemarksCount: productRemarks.length,
              productRemarksLinesCount: productRemarksLinesUsed.length,
              productRemarksPreview: String(productRemarksTextUsed).slice(0, 220),
              extraItemRemarkStats: item?.__pdfExtraItemRemarkDebug,
              extraItemRemarkPreview: String(item?.__pdfExtraItemRemarkDebug?.text ?? "").slice(0, 220),
              remarkBlocksCount: remarkBlocks.length,
              remarkBlocksPreviews: remarkBlocks.map((t) => String(t).slice(0, 120)),
            });
          }
        }
      } catch (_) { }

      // Cleanup debug attachment to avoid mutating item data long-term.
      try {
        if (Object.prototype.hasOwnProperty.call(item, "__pdfExtraItemRemarkDebug")) {
          delete item.__pdfExtraItemRemarkDebug;
        }
      } catch (_) { }

      if (remarkBlocks.length > 0) {
        descCell = `${descCell}\n${remarkBlocks.join("\n")}`;
      }

      const quantity =
        item.ProductQuan ??
        item.Quantity ??
        item.quantity ??
        item.Quantity1 ??
        item.quantity1 ??
        0;
      const price = item.ProductPrice || 0;
      const discount = item.LineDiscountAmount || 0;
      const amount = quantity * price - discount;
      const unit = String(item.ProductUnit ?? item.Unit ?? item.Unit1 ?? item.unit ?? "").trim();

      const qtyText = hasSecondUnitInfo(item) ? numberFormat(quantity) : formatQtyNoDecimal(quantity);

      tableRows.push([
        startIndex + index + 1,
        productCode,
        descCell,
        `${qtyText} ${unit}`.trim(),
        numberFormat(price),
        numberFormat(discount),
        numberFormat(amount),
      ]);
    });

    return tableRows;
  }

  getExtraConditionsFooterRows() {
    const extraConditionsRaw =
      (Array.isArray(this.data?.ExtraConditions) && this.data.ExtraConditions) ||
      (Array.isArray(this.data?.extraConditions) && this.data.extraConditions) ||
      [];

    const normalizeText = (v) => {
      if (v === null || v === undefined) return "";
      return String(v).replace(/\s+/g, " ").trim();
    };

    const normalizeKeyLoose = (key) => String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const getLoose = (obj, key) => {
      if (!obj || typeof obj !== "object") return undefined;
      if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
      const target = normalizeKeyLoose(key);
      for (const k of Object.keys(obj)) {
        if (normalizeKeyLoose(k) === target) return obj[k];
      }
      return undefined;
    };

    const formatValueForPdf = ({ valueRaw, condType }) => {
      if (valueRaw === null || valueRaw === undefined) return "";
      if (typeof valueRaw === "number") {
        if (!Number.isFinite(valueRaw)) return "";
        const ct = normalizeText(condType);
        const isPercent = ct && ct.toLowerCase().includes("percent");
        if (isPercent) return `${numberFormat(valueRaw, 2)}%`;

        // Likely lookup codes (e.g. 1, 3, 35) should not become 1.00/3.00/35.00
        if (Number.isInteger(valueRaw) && Math.abs(valueRaw) <= 999) return String(valueRaw);

        return numberFormat(valueRaw, 2);
      }

      const raw = String(valueRaw);
      const trimmed = raw.trim();
      if (!trimmed) return "";

      // Keep percent as-is
      if (trimmed.includes("%")) return normalizeText(trimmed);

      // Only format when the whole value is numeric-ish (avoid changing strings like "30 วัน").
      const compact = trimmed.replace(/\s+/g, "");

      // Keep codes like 01, 03 (do not format to 1.00)
      if (/^0\d+$/.test(compact)) return trimmed;

      // Keep small integers (e.g. 35) as-is; these are often lookup codes.
      if (/^[+-]?\d+$/.test(compact) && compact.replace(/^[+-]/, "").length <= 3) {
        // But if this is a *Percent field and missing '%', add it.
        const ct = normalizeText(condType);
        if (ct && ct.toLowerCase().includes("percent") && !trimmed.includes("%")) return `${trimmed}%`;
        return trimmed;
      }

      // Handle accounting negative: (1234.50)
      const isParenNegative = compact.startsWith("(") && compact.endsWith(")");
      const core = isParenNegative ? compact.slice(1, -1) : compact;

      const numericPattern1 = /^[+-]?\d{1,3}(,\d{3})*(\.\d+)?$/; // 1,234.56
      const numericPattern2 = /^[+-]?\d+(\.\d+)?$/; // 1234.56
      if (!numericPattern1.test(core) && !numericPattern2.test(core)) {
        // Percent: add suffix when missing and value is numeric-ish text
        const ct = normalizeText(condType);
        if (ct && ct.toLowerCase().includes("percent") && /^[+-]?\d+(\.\d+)?$/.test(compact) && !trimmed.includes("%")) {
          return `${trimmed}%`;
        }
        return normalizeText(raw);
      }

      const n = Number(core.replace(/,/g, ""));
      if (!Number.isFinite(n)) return normalizeText(raw);

      const out = isParenNegative ? `(${numberFormat(Math.abs(n), 2)})` : numberFormat(n, 2);
      const ct = normalizeText(condType);
      if (ct && ct.toLowerCase().includes("percent") && !out.includes("%")) return `${out}%`;
      return out;
    };

    const shouldShowOnPrint = (row) => {
      const flag = normalizeText(getLoose(row, "ShowOnPrint") ?? getLoose(row, "showonprint"));
      if (!flag) return true;
      return flag.toUpperCase() === "Y";
    };

    const ensureLabelColon = (label) => {
      const s = normalizeText(label);
      if (!s) return "";
      if (/[：:]\s*$/.test(s)) return s;
      return `${s} :`;
    };

    return extraConditionsRaw

      .filter((r) => r && typeof r === "object")
      .filter(shouldShowOnPrint)
      .slice()
      .sort((a, b) => {
        const sa = Number(a?.SeqNo ?? 0);
        const sb = Number(b?.SeqNo ?? 0);
        if (sa !== sb) return sa - sb;
        return normalizeText(a?.CondType ?? a?.Condtype).localeCompare(normalizeText(b?.CondType ?? b?.Condtype));
      })
      .map((c) => {
        const condType = normalizeText(getLoose(c, "CondType") ?? getLoose(c, "Condtype"));
        const labelKey = normalizeText(getLoose(c, "LabelKey") ?? getLoose(c, "labelkey")) || condType || "";

        // New API contract: left always uses labelvalue
        const labelValue = normalizeText(getLoose(c, "LabelValue") ?? getLoose(c, "labelvalue"));
        const fallbackLabel =
          normalizeText(getLoose(c, "Description")) ||
          labelKey ||
          "";

        // New API contract: show labelvalue as-is (no translation).
        const label = labelValue || fallbackLabel;

        const lookupDesc = normalizeText(getLoose(c, "LookupDescription") ?? getLoose(c, "lookupdescription"));
        const valueRaw = lookupDesc ? lookupDesc : (getLoose(c, "Value") ?? getLoose(c, "value"));

        const value = formatValueForPdf({ valueRaw, condType }) || "-";

        return {
          subject: ensureLabelColon(label),
          remarks: value,
        };
      })
      .filter((r) => r.subject);
  }

  getCombinedFooterRemarkRows() {
    const headerRow = this.getRemarkFooterHeaderRow();
    const extraRows = this.getExtraConditionsFooterRows();
    const productFooterRows = this.getProductRemarksFooterRows();
    const productRows = (productFooterRows || [])
      .map((r) => ({ subject: String(r?.subject || "").trim(), remarks: String(r?.remarks || "").trim() }))
      .filter((r) => r.subject || r.remarks);
    return [headerRow, ...extraRows, ...productRows].filter((r) => r && (r.subject || r.remarks));
  }

  getItemTableLayout(pageW) {
    const configuredColumnWidths = this.getItemTableColumnWidths();
    const tableW = configuredColumnWidths.reduce((sum, v) => sum + (Number(v) || 0), 0);
    const tableX = Math.max(0, (Number(pageW || 0) - tableW) / 2);
    const margin = {
      left: tableX,
      right: Math.max(0, Number(pageW || 0) - tableX - tableW),
    };
    return { tableX, tableW, margin };
  }

  getItemTableAutoTableOptions({ startY, tableRows, pageW }) {
    const { margin, tableW } = this.getItemTableLayout(pageW);

    // Track the real bottom Y of the last body row per page.
    // This is more reliable than `data.cursor.y` across page breaks.
    const pageBodyBottomY = Object.create(null);

    return {
      startY,
      head: this.getItemTableHead(),
      body: tableRows,
      margin,
      tableWidth: tableW,
      theme: "grid",
      styles: {
        font: this.fontName,
        fontSize: this.baseFontSize,
        lineHeightFactor: this.lineHeightFactor,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        cellPadding: { top: 2.8, right: 2, bottom: 2.8, left: 2 },
        minCellHeight: 7,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      },
      didParseCell: (hookData) => {
        if (hookData.section === "head") {
          const colIndex = hookData.column.index;

          // Make Thai/English header rows closer together
          if (hookData.row.index === 0) {
            // Thai row: keep a bit more top padding, minimal bottom padding
            hookData.cell.styles.cellPadding = { top: 0.8, right: 2, bottom: 0.2, left: 2 };
          } else {
            // English row: minimal top padding, keep a bit more bottom padding
            hookData.cell.styles.cellPadding = { top: 0.2, right: 2, bottom: 0.8, left: 2 };
          }
          hookData.cell.styles.minCellHeight = 0;

          if (hookData.row.index === 0) {
            hookData.cell.styles.lineWidth = { top: 0.2, right: 0.2, bottom: 0, left: 0.2 };
          } else {
            hookData.cell.styles.lineWidth = { top: 0, right: 0.2, bottom: 0.2, left: 0.2 };
          }

          if (colIndex === 1) hookData.cell.styles.lineWidth.right = 0;
          if (colIndex === 2) hookData.cell.styles.lineWidth.left = 0;
          return;
        }

        if (hookData.section === "body") {
          const colIndex = hookData.column.index;
          hookData.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            // IMPORTANT: Do not depend on "last row of whole table" for the bottom border.
            // autoTable can split tables across pages, so we close the border per-page in `didDrawPage`.
            bottom: 0,
            left: 0.2,
          };

          if (colIndex === 1) hookData.cell.styles.lineWidth.right = 0;
          if (colIndex === 2) hookData.cell.styles.lineWidth.left = 0;
        }
      },
      didDrawPage: (hookData) => {
        // When autoTable breaks across pages, the body section on a page may not contain
        // the final row of the entire table, causing the bottom border to be missing.
        // Close the table on every page using the cursor Y after drawing that page.
        const doc = hookData?.doc;
        const pageNumber = Number(hookData?.pageNumber);
        const trackedBottomY = Number.isFinite(pageNumber)
          ? Number(pageBodyBottomY[pageNumber])
          : NaN;
        const cursorY = Number(hookData?.cursor?.y);
        const bottomY = Number.isFinite(trackedBottomY) ? trackedBottomY : cursorY;
        // Use configured geometry; hookData.table.x/width is not guaranteed.
        const x1 = Number(margin?.left);
        const w = Number(tableW);

        if (!doc || !Number.isFinite(bottomY) || !Number.isFinite(x1) || !Number.isFinite(w)) return;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.2);
        doc.line(x1, bottomY, x1 + w, bottomY);
      },
      didDrawCell: (hookData) => {
        // Track bottom position for each page (body only)
        if (hookData.section === "body") {
          const pageNumber = Number(hookData?.pageNumber);
          const cellY = Number(hookData?.cell?.y);
          const cellH = Number(hookData?.cell?.height);
          if (Number.isFinite(pageNumber) && Number.isFinite(cellY) && Number.isFinite(cellH)) {
            const bottom = cellY + cellH;
            const prev = Number(pageBodyBottomY[pageNumber]);
            pageBodyBottomY[pageNumber] = Number.isFinite(prev) ? Math.max(prev, bottom) : bottom;
          }
        }

        if (hookData.section !== "head") return;
        if (hookData.row.index !== 1) return;

        const doc = hookData.doc;
        const x1 = Number(hookData.cell?.x);
        const x2 = x1 + Number(hookData.cell?.width);
        const y = Number(hookData.cell?.y) + Number(hookData.cell?.height);
        if (!Number.isFinite(x1) || !Number.isFinite(x2) || !Number.isFinite(y)) return;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.25);
        doc.line(x1, y, x2, y);
      },
      columnStyles: {
        0: { cellWidth: 12, halign: "center", valign: "top" },
        1: { cellWidth: 28, halign: "left", valign: "top" },
        2: { cellWidth: 59, halign: "left", valign: "top" },
        3: { cellWidth: 18, halign: "right", valign: "top" },
        4: { cellWidth: 27, halign: "right", valign: "top" },
        5: { cellWidth: 27, halign: "right", valign: "top" },
        6: { cellWidth: 27, halign: "right", valign: "top" },
      },
    };
  }

  measureItemTableFinalY({ startY, tableRows, pageW }) {
    const doc = new jsPDF(this.pdfOption);
    try {
      doc.setFont(this.fontName, this.resolveFontStyle("normal", doc));
    } catch (_) { }
    doc.setFontSize(this.baseFontSize);

    const opts = this.getItemTableAutoTableOptions({ startY, tableRows, pageW });
    doc.autoTable(opts);
    return Number(doc.lastAutoTable?.finalY) || startY;
  }

  measureFooterRemarksFit({ pdf, x, y, w, maxHeight, rows, forcePlaceholder = true }) {
    let effectiveRows = rows.length ? rows : forcePlaceholder ? [{ subject: "-", remarks: "" }] : [];

    // Widow/orphan control: if the first row is the header, it must be rendered together
    // with at least one more row on the same page (header should not be alone).
    // If caller passes only the header row, treat it as header + blank placeholder.
    if (effectiveRows.length === 1 && this.isRemarkFooterHeaderRow(effectiveRows[0])) {
      effectiveRows = [...effectiveRows, { subject: "", remarks: "" }];
    }

    if (!effectiveRows.length) return { fitsAll: true, height: 0 };
    if (!Number.isFinite(maxHeight) || maxHeight <= 0) return { fitsAll: false, height: 0 };

    const ptToMm = 0.3527777778;
    const basePt = this.baseFontSize;
    // IMPORTANT: Keep footer remark font size consistent across pages.
    // If it doesn't fit, paginate (move rows) instead of shrinking text.
    const style = { fontSizePt: Math.max(7, basePt), lineHeightFactor: this.lineHeightFactor, padX: 2, padY: 2 };

    const computeFit = ({ fontSizePt, lineHeightFactor, padX, padY }) => {
      pdf.setFontSize(fontSizePt);
      const lineHeightMm = fontSizePt * ptToMm * lineHeightFactor;

      const gap = 3;
      pdf.setFont(this.fontName, this.resolveFontStyle("bold", pdf));
      const maxSubjectTextW = effectiveRows.reduce((maxW, r) => {
        const t = String(r?.subject || "-");
        return Math.max(maxW, pdf.getTextWidth(t));
      }, 0);

      const minSubjectColW = 28;
      const maxSubjectColW = Math.max(minSubjectColW, Math.min(55, w * 0.45));
      const subjectColW = Math.min(maxSubjectTextW + 2, maxSubjectColW);
      const subjectColWFinal = Math.max(minSubjectColW, subjectColW);

      const subjectX = x + padX;
      const remarksX = subjectX + subjectColWFinal + gap;
      const remarksW = Math.max(0, x + w - padX - remarksX);

      const measureRowHeight = (subject, remarks) => {
        const subjectText = String(subject || "-");
        const subjectLines = pdf.splitTextToSize(subjectText, subjectColWFinal);

        pdf.setFont(this.fontName, this.resolveFontStyle("normal", pdf));
        const remarksText = String(remarks || "").trim();
        const remarkLines = remarksText ? pdf.splitTextToSize(remarksText, remarksW) : [""];

        const lines = Math.max(Math.max(1, subjectLines.length), Math.max(1, remarkLines.length));
        // Keep rows tight; avoid blank-line-like gaps when remarks are split into multiple numbered subjects.
        return lines * lineHeightMm + padY * 0.4;
      };

      let totalH = padY;
      let lastRowIndex = -1;
      for (let i = 0; i < effectiveRows.length; i++) {
        const h = measureRowHeight(effectiveRows[i].subject, effectiveRows[i].remarks);
        if (totalH + h + padY > maxHeight) break;
        totalH += h;
        lastRowIndex = i;
      }

      return { totalH, padY, lastRowIndex };
    };

    const fit = computeFit(style);

    // Enforce minimum rows when starting with the header row.
    const minRequiredRows = this.isRemarkFooterHeaderRow(effectiveRows[0]) ? 2 : 1;
    if (minRequiredRows > 1 && fit.lastRowIndex < minRequiredRows - 1) {
      return { fitsAll: false, height: 0, lastRowIndex: -1 };
    }

    const fitsAll = fit.lastRowIndex >= effectiveRows.length - 1;
    const height = fit.lastRowIndex < 0 ? 0 : fit.totalH + fit.padY;
    return { fitsAll, height, lastRowIndex: fit.lastRowIndex };
  }

  async generate(pdfInstance = null) {
    if (pdfInstance) {
      this.pdf = pdfInstance;
      this.pdf.addPage();
    } else {
      this.pdf = new jsPDF(this.pdfOption);
    }

    // Ensure Thai text with stacking marks doesn't overlap between lines.
    try {
      if (typeof this.pdf?.setLineHeightFactor === "function") {
        this.pdf.setLineHeightFactor(this.lineHeightFactor);
      }
    } catch (_) { }

    this.pdf.setProperties({
      title: `Quotation-${this.data.DocNumber}`,
      subject: "Quotation Transaction",
      author: "ISA System",
    });

    const items = this.data.QuotationTransactionItem || [];
    const footerRows = this.getCombinedFooterRemarkRows();

    let cursor = 0;
    let pageIndex = 0;

    while (cursor < items.length || pageIndex === 0) {
      if (pageIndex > 0) this.pdf.addPage();

      this.yAxis = this.pdfConfig.margin.t;
      this.width = this.pdf.internal.pageSize.width;
      this.height = this.pdf.internal.pageSize.height;

      await this.renderHeader();
      if (this.showInfoSection) {
        await this.renderInfoSection();
      }

      const baseStartYNoInfoText = this.yAxis;

      // Show intro text only once (first page only)
      if (cursor === 0) {
        await this.renderInfoText();
      }

      const remaining = items.length - cursor;
      const pageCap = Math.min(this.rowsPerPage, remaining);

      // Special handling for the final chunk when footer remarks exist:
      // keep (last items + summary/remark + footer remarks) on the same page.
      let takeCount = pageCap;
      const isFinalChunkCandidate = cursor + pageCap >= items.length;

      // Final page must reserve space for footer.png even when footer rows are empty
      // (we still render the footer table grid/placeholder).
      if (isFinalChunkCandidate && !this.summaryOnEveryPage) {
        const pageW = this.width;
        // Last page must keep room for footer.png (rendered only on the final page).
        const maxY = this.getMaxContentY({ includeFooterImage: true });
        const summaryH = this.getSummaryAndRemarkHeight();
        const { tableW } = this.getItemTableLayout(pageW);

        const canFitLastPage = (lastItems, lastStartIndex, lastStartY) => {
          const rows = this.buildItemTableRows(lastItems, lastStartIndex);
          const tableEndY = this.measureItemTableFinalY({
            startY: lastStartY,
            tableRows: rows,
            pageW,
          });

          if (!Number.isFinite(tableEndY)) return false;
          if (tableEndY + summaryH > maxY) return false;

          // Footer remarks table starts right after summary/remark.
          const footerMaxHeight = maxY - (tableEndY + summaryH);
          if (!Number.isFinite(footerMaxHeight) || footerMaxHeight <= 0) return false;
          const measureDoc = new jsPDF(this.pdfOption);
          try {
            measureDoc.setFont(this.fontName, this.resolveFontStyle("normal", measureDoc));
          } catch (_) { }
          measureDoc.setFontSize(this.baseFontSize);

          const fit = this.measureFooterRemarksFit({
            pdf: measureDoc,
            x: 0,
            y: 0,
            w: tableW,
            maxHeight: footerMaxHeight,
            rows: footerRows,
            forcePlaceholder: true,
          });
          // Footer can paginate to the next page; we only need to ensure
          // that at least one row/placeholder can start on this last page.
          return Number.isFinite(fit?.lastRowIndex) && Number(fit.lastRowIndex) >= 0;
        };

        // If everything fits on this page already, keep as-is.
        const allItems = items.slice(cursor, cursor + pageCap);
        const fitsAllHere = canFitLastPage(allItems, cursor, this.yAxis);

        if (!fitsAllHere) {
          // Find the minimum number of items that can live on the last page
          // with summary+footer remarks (so only the tail moves, including the last item's "- remark").
          const remainingItems = items.slice(cursor, cursor + pageCap);
          let lastPageCount = -1;
          for (let n = 1; n <= remainingItems.length; n++) {
            const lastItems = remainingItems.slice(remainingItems.length - n);
            const lastStartIndex = cursor + (remainingItems.length - n);
            if (canFitLastPage(lastItems, lastStartIndex, baseStartYNoInfoText)) {
              lastPageCount = n;
              break;
            }
          }

          if (lastPageCount > 0 && lastPageCount < remainingItems.length) {
            // Render only the prefix on this page; the tail goes to a new page.
            takeCount = remainingItems.length - lastPageCount;
          }
        }
      }

      const pageItems = items.slice(cursor, cursor + takeCount);
      await this.renderItemTable(pageItems, cursor, { skipInfoText: true });
      cursor += takeCount;

      const isLastPageNow = cursor >= items.length;
      if (this.summaryOnEveryPage || isLastPageNow) {
        await this.renderSummaryAndRemarkSection();
      }
      pageIndex += 1;
    }

    // Render footer image only on the final page (maximize content on other pages).
    const totalPagesForFooter = this.pdf.getNumberOfPages();
    this.pdf.setPage(totalPagesForFooter);
    this.width = this.pdf.internal.pageSize.width;
    this.height = this.pdf.internal.pageSize.height;
    await this.renderFooter();

    // Render pagination after all pages are created (so total page count is correct)
    const totalPages = this.pdf.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      this.pdf.setPage(p);
      this.width = this.pdf.internal.pageSize.width;
      this.height = this.pdf.internal.pageSize.height;
      this.renderPagination(p, totalPages);
    }

    return this.pdf;
  }

  renderPagination(currentPage, totalPages) {
    this.setFont(-1);
    const { r } = this.pdfConfig.margin;

    // Footer image is rendered on the last page only; keep page number in bottom margin.
    // (Bottom margin is small; use a safe constant to avoid overlapping content.)
    const pageNumberY = this.height - 2;

    this.pdf.text(`หน้า ${currentPage} / ${totalPages}`, this.width - r - 5, pageNumberY, {
      align: "right",
    });
  }

  async renderHeader() {
    const pdf = this.pdf;
    const { l, r, t } = this.pdfConfig.margin;

    // If user selected a header template (dropdown), do NOT force company header.
    if (this.isStrictCompanyHeaderFooterEnabled() && !this.hasDynamicHeaderSelection()) {
      try {
        const resolved = await this.resolveHeaderImageSourceStrict();
        const maxW = this.width;

        const getImageSize = (src) => {
          try {
            if (typeof Image !== "undefined" && src instanceof Image) {
              const w = Number(src.naturalWidth || src.width);
              const h = Number(src.naturalHeight || src.height);
              return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
            }
          } catch (_) { }

          try {
            if (typeof pdf?.getImageProperties === "function") {
              const p = pdf.getImageProperties(src);
              const w = Number(p?.width);
              const h = Number(p?.height);
              return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
            }
          } catch (_) { }

          return null;
        };

        const size = getImageSize(resolved.source);
        let drawW = maxW;
        let drawH = 40;
        if (size) {
          const imgRatio = size.w / size.h;
          drawW = maxW;
          drawH = Math.max(1, maxW / imgRatio);
        }

        pdf.addImage(resolved.source, resolved.type || "PNG", 0, 0, drawW, drawH);
        const gap = 3;
        this.yAxis = Math.max(t, 0 + drawH + gap);
        return;
      } catch (e) {
        if (this.allowMissingCompanyHeaderFooter()) {
          // Keep the same vertical space so layout remains stable.
          this.yAxis = t + 45;
          return;
        }
        const err = new Error("PDF_HEADER_NOT_FOUND_OR_UNUSABLE");
        err.cause = e;
        throw err;
      }
    }

    const company = String(
      this.data?.Company ?? this.data?.QuotationTransaction?.Company ?? ""
    ).trim();
    const code = String(this.options?.docHeaderTypeCode ?? "").trim();
    const imageFilename = String(this.options?.docHeaderImageFilename ?? "").trim();
    const imageFileLocation = String(this.options?.docHeaderImageFileLocation ?? "").trim();

    const buildHeaderCandidates = () => {
      const out = [];

      // Highest priority: filename resolved by mapping API
      if (imageFilename) {
        out.push(imageFilename);
      }

      // If API sends a full filename (e.g. 97_header_quotation.png)
      if (code) {
        if (/[\\/]/.test(code) || /\.(png|jpe?g)$/i.test(code)) {
          out.push(code);
        } else {
          out.push(`${code}.png`, `${code}.jpg`, `${code}.jpeg`);
          if (company) {
            out.push(`${company}_${code}.png`, `${company}_${code}.jpg`, `${company}_${code}.jpeg`);
          }
        }
      }

      // Default per company if exists
      if (company) {
        out.push(
          `${company}_header_quotation.png`,
          `${company}_header_quotation.jpg`,
          `${company}_header_quotation.jpeg`
        );
      }

      // Final fallback
      out.push("97_header_quotation.png");
      return out;
    };

    const candidates = buildHeaderCandidates();
    let headerSource = null;
    let headerType = "PNG";

    const buildHeaderUrlCandidate = (fileName) => {
      const f = String(fileName || "").trim();
      if (!f) return "";

      // If filename already contains a path, treat it as relative to FILE_URL.
      if (/[\\/]/.test(f)) {
        const base = getDocumentBaseUrl();
        return joinUrl(base, f);
      }

      // Default: use provided file location, falling back to "form".
      return buildFileImageUrlFromEnv({
        fileLocation: imageFileLocation || "",
        fileName: f,
        defaultLocation: "",
      });
    };

    const headerCandidates = [];
    for (const fileName of candidates) {
      const envUrl = buildHeaderUrlCandidate(fileName);
      if (envUrl) {
        headerCandidates.push({ src: envUrl, type: inferImageType(fileName) });
      }

      const localSrc = tryLoadFormImage(fileName);
      if (localSrc) {
        headerCandidates.push({ src: localSrc, type: inferImageType(fileName) });
      }
    }

    // Final fallback to default bundled header.
    headerCandidates.push({ src: defaultHeaderImg, type: "PNG" });

    for (const c of headerCandidates) {
      const resolved = await resolveImageForJsPdf(c);
      if (resolved?.source) {
        headerSource = resolved.source;
        headerType = resolved.type || "PNG";
        break;
      }
    }

    try {
      // แสดงภาพ header (รักษาสัดส่วนภาพ ไม่บีบ/ยืด)
      // Requirement: expand to full page width and derive height from the image aspect ratio.
      // This prevents the header from being unintentionally shrunk when the source image is taller.
      const maxW = this.width;

      const getImageSize = (src) => {
        try {
          if (typeof Image !== "undefined" && src instanceof Image) {
            const w = Number(src.naturalWidth || src.width);
            const h = Number(src.naturalHeight || src.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
          }
        } catch (_) { }

        try {
          if (typeof pdf?.getImageProperties === "function") {
            const p = pdf.getImageProperties(src);
            const w = Number(p?.width);
            const h = Number(p?.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
          }
        } catch (_) { }

        return null;
      };

      const size = getImageSize(headerSource);
      let drawW = maxW;
      let drawH = 40;
      if (size) {
        const imgRatio = size.w / size.h;
        // Fit by width only (full bleed horizontally); derive height from aspect ratio.
        drawW = maxW;
        drawH = Math.max(1, maxW / imgRatio);
      }

      const x = 0;
      const y = 0;
      pdf.addImage(headerSource, headerType, x, y, drawW, drawH);

      // ปรับ yAxis ให้อยู่หลังภาพ header ตามความสูงจริงของรูป
      // Keep at least the configured top margin after the header.
      const gap = 3;
      this.yAxis = Math.max(t, y + drawH + gap);
      return;
    } catch (e) {
    }

    // Fallback: ปรับ yAxis ให้อยู่หลังภาพ header
    this.yAxis = t + 45;
  }

  async renderFooter() {
    const pdf = this.pdf;
    const { l, r, b } = this.pdfConfig.margin;

    if (this.isStrictCompanyHeaderFooterEnabled()) {
      try {
        const resolved = await this.resolveFooterImageSourceStrict();
        const footerHeightMax = this.getFooterImageHeight();

        const { tableX, tableW, margin } = this.getItemTableLayout(this.width);
        const baseX = Number.isFinite(tableX) ? tableX : Number(margin?.left) || l;
        const baseW = Number.isFinite(tableW) && tableW > 0 ? tableW : this.width - l - r;
        const bleedX = 0.3;
        const footerX = Math.max(0, baseX - bleedX);
        const footerW = Math.min(this.width - footerX, baseW + bleedX * 2);

        const getImageSize = (src) => {
          try {
            if (typeof Image !== "undefined" && src instanceof Image) {
              const w = Number(src.naturalWidth || src.width);
              const h = Number(src.naturalHeight || src.height);
              return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
            }
          } catch (_) { }

          try {
            if (typeof pdf?.getImageProperties === "function") {
              const p = pdf.getImageProperties(src);
              const w = Number(p?.width);
              const h = Number(p?.height);
              return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
            }
          } catch (_) { }

          return null;
        };

        const size = getImageSize(resolved.source);
        let drawW = footerW;
        let drawH = footerHeightMax;
        if (size) {
          const ratio = size.w / size.h;
          if (Number.isFinite(ratio) && ratio > 0) {
            drawW = footerW;
            drawH = Math.max(1, footerW / ratio);
          }
        }

        const footerBottomY = this.height - b - drawH;
        const footerY = footerBottomY;
        pdf.addImage(resolved.source, resolved.type || "PNG", footerX, footerY, drawW, drawH);

        // Overlay salesman signature on the left side of the footer.
        await this.tryRenderSalesmanSignatureOnFooter({
          footerX,
          footerY,
          footerW: drawW,
          footerH: drawH,
        });
        return;
      } catch (e) {
        if (this.allowMissingCompanyHeaderFooter()) {
          return;
        }
        const err = new Error("PDF_FOOTER_NOT_FOUND_OR_UNUSABLE");
        err.cause = e;
        throw err;
      }
    }

    try {
      // แสดงภาพ footer ที่ด้านล่างของหน้า
      const footerHeightMax = this.getFooterImageHeight();

      // Place footer.png snugly under the last footer-remarks table on this page (if known).
      // Clamp to bottom position to avoid going off-page.
      const page = this.getCurrentPageNumber(pdf);
      const totalPages = typeof pdf?.getNumberOfPages === "function" ? Number(pdf.getNumberOfPages()) : NaN;

      // Prefer the explicit last-page anchor (more reliable than page map lookups).
      let bottomY = NaN;
      if (Number.isFinite(totalPages) && page === totalPages) {
        const anchorPage = Number(this.lastFooterRemarksPage);
        const anchorY = Number(this.lastFooterRemarksBottomY);
        if (Number.isFinite(anchorPage) && Number.isFinite(anchorY) && anchorPage === totalPages) {
          bottomY = anchorY;
        }
      }

      if (!Number.isFinite(bottomY) && page) {
        bottomY = Number(this.pageFooterRemarksBottomY[page]);
      }
      const gap = this.getFooterImageTopGap();
      const desiredY = Number.isFinite(bottomY) ? bottomY + gap : NaN;

      // Align footer image exactly to the same geometry as the item table.
      // Use the same layout function autoTable uses (single source of truth).
      const { tableX, tableW, margin } = this.getItemTableLayout(this.width);
      const baseX = Number.isFinite(tableX) ? tableX : Number(margin?.left) || l;
      const baseW = Number.isFinite(tableW) && tableW > 0 ? tableW : this.width - l - r;

      // Expand slightly beyond table borders for better visual alignment.
      const bleedX = 0.3;
      const footerX = Math.max(0, baseX - bleedX);
      const footerW = Math.min(this.width - footerX, baseW + bleedX * 2);

      const resolved = await this.resolveFooterImageSource();

      const getImageSize = (src) => {
        try {
          if (typeof Image !== "undefined" && src instanceof Image) {
            const w = Number(src.naturalWidth || src.width);
            const h = Number(src.naturalHeight || src.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
          }
        } catch (_) { }

        try {
          if (typeof pdf?.getImageProperties === "function") {
            const p = pdf.getImageProperties(src);
            const w = Number(p?.width);
            const h = Number(p?.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : null;
          }
        } catch (_) { }

        return null;
      };

      // Preserve aspect ratio (avoid squashing) and prefer matching table width.
      const size = getImageSize(resolved.source);
      let drawW = footerW;
      let drawH = footerHeightMax;
      if (size) {
        const ratio = size.w / size.h;
        if (Number.isFinite(ratio) && ratio > 0) {
          // Fit by width to match table borders; height follows aspect ratio.
          drawW = footerW;
          drawH = Math.max(1, footerW / ratio);
        }
      }

      const footerBottomY = this.height - b - drawH;
      const footerY = Number.isFinite(desiredY) ? Math.min(footerBottomY, desiredY) : footerBottomY;

      pdf.addImage(resolved.source, resolved.type || "PNG", footerX, footerY, drawW, drawH);

      // Overlay salesman signature on the left side of the footer.
      await this.tryRenderSalesmanSignatureOnFooter({
        footerX,
        footerY,
        footerW: drawW,
        footerH: drawH,
      });
    } catch (e) {
      try {
        // eslint-disable-next-line no-console
        console.error("[QuotationPdf] renderFooter failed", e);
      } catch (_) { }
    }
  }

  async renderInfoSection() {
    const pdf = this.pdf;
    const { x: formX, w: formW, midX } = this.getFormBox();

    // Some APIs may return QuotationTransaction as an array.
    const qtTransactionRaw = this.data.QuotationTransaction;
    const qtTransaction = Array.isArray(qtTransactionRaw) ? qtTransactionRaw[0] : qtTransactionRaw;
    const customer = this.data.Customer;
    const contact = this.data.Contact;

    const docDateMoment = (() => {
      const d = qtTransaction?.DocDate ?? this.data?.DocDate;
      if (!d) return null;
      const m = moment(String(d).trim(), "YYYYMMDD", true);
      return m.isValid() ? m : null;
    })();

    const extractFirstInt = (raw) => {
      if (raw === null || raw === undefined) return null;
      const s = String(raw).trim();
      if (!s) return null;
      const m = s.match(/-?\d+/);
      if (!m) return null;
      const n = Number(m[0]);
      return Number.isFinite(n) ? n : null;
    };

    const parseDayOffsetsCsv = (raw) => {
      if (raw === null || raw === undefined) return [];
      const s = String(raw).trim();
      if (!s) return [];

      return s
        .split(",")
        .map((p) => String(p).trim())
        .filter(Boolean)
        .map((p) => {
          const n = Number(p);
          return Number.isFinite(n) ? n : NaN;
        })
        .filter((n) => Number.isFinite(n));
    };

    const normalizePersonText = (val) => {
      const s = String(val ?? "").trim();
      if (!s) return "";
      const lower = s.toLowerCase();
      if (s === "-" || lower === "undefined" || lower === "null") return "";
      return s;
    };

    const contactName =
      contact?.ContactName ||
      contact?.ContactNameTH ||
      contact?.ContactNameTh ||
      contact?.Name ||
      "";
    const contactEmail =
      contact?.Email ||
      contact?.EMail ||
      contact?.email ||
      customer?.Email ||
      customer?.EMail ||
      "";
    const contactTel = contact?.ContactNo || contact?.TelephoneNumber || contact?.Phone || "";
    const contactNameClean = normalizePersonText(contactName);
    const contactTitleRaw = normalizePersonText(contact?.Title ?? contact?.ContactTitle);
    const contactTitle = contactNameClean && !contactTitleRaw ? "คุณ" : contactTitleRaw;
    const customerName = customer?.CustomerName || qtTransaction?.CustomerName || qtTransaction?.Bill_ShipName || "";
    const customerCode = customer?.CustomerCode || qtTransaction?.CustomerCode || "";

    const pickText = (...vals) => {
      for (const v of vals) {
        if (v === null || v === undefined) continue;
        const s = String(v).trim();
        if (s) return s;
      }
      return "";
    };

    const joinParts = (parts, sep = " ") =>
      parts
        .map((p) => (p === null || p === undefined ? "" : String(p).trim()))
        .filter(Boolean)
        .join(sep);

    // Address can come in different shapes depending on API/model
    const customerAddress = pickText(
      qtTransaction?.ShipAddress,
      customer?.ShipAddress,
      customer?.Address?.Customer,
      customer?.Address,
      customer?.LegalAddress,
      customer?.CustomerAddress,
      customer?.Bill_ShipAddress,
      qtTransaction?.CustomerAddress,
      qtTransaction?.Bill_ShipAddress,
      // common multi-field address patterns
      joinParts([
        customer?.Address1 || customer?.Address_1 || customer?.AddressLine1,
        customer?.Address2 || customer?.Address_2 || customer?.AddressLine2,
        customer?.Address3 || customer?.Address_3 || customer?.AddressLine3,
        customer?.Subdistrict || customer?.SubDistrict || customer?.Tambon,
        customer?.District || customer?.Amphur || customer?.Amphoe,
        customer?.Province,
        customer?.ZipCode || customer?.Zipcode || customer?.Postcode || customer?.PostalCode,
      ])
    );
    const customerTax =
      customer?.TaxNumber || qtTransaction?.TaxNumber || "";

    let leftY = this.yAxis + 5;
    const leftStartY = leftY;

    const renderLeftRow = (label, value) => {
      leftY = this.renderLabelValueBlock({
        pdf,
        label,
        value,
        x: formX + 2,
        y: leftY,
        maxWidth: midX - formX - 4,
      });
    };

    // ซ้าย: เรียงตามที่ขอ
    renderLeftRow("เรียน/ To :", [contactTitle, contactNameClean].filter(Boolean).join(" "));
    renderLeftRow(
      "รหัสลูกค้า/ CustomerCode :",
      `${customerName || ""}${customerName && customerCode ? ` (${customerCode})` : customerCode ? `(${customerCode})` : ""}`.trim()
    );
    renderLeftRow("ที่อยู่/ Address :", customerAddress);
    renderLeftRow(
      "Tel :",
      `${contactTel || ""}`
    );
    renderLeftRow("Email :", contactEmail);
    renderLeftRow("เลขประจำตัวผู้เสียภาษี/ TaxID :", customerTax);

    const leftHeight = leftY - leftStartY + 3;

    let rightY = this.yAxis + 5;
    const rightStartY = rightY;

    const renderRightRow = (label, value) => {
      rightY = this.renderLabelValueBlock({
        pdf,
        label,
        value,
        x: midX + 2,
        y: rightY,
        // right box width = (formX + formW - midX), keep 2mm padding each side
        maxWidth: formX + formW - midX - 4,
      });
    };

    const renderRightTopRowWithStatus = ({ docNo, statusText }) => {
      const x = midX + 2;
      const y = rightY;
      const maxWidth = formX + formW - midX - 4;
      const rightEdge = x + maxWidth;
      const lineHeight = 5;

      const label = "เลขที่/ No : ";
      const status = String(statusText || "").trim();

      this.setFont(0, "bold");
      pdf.setTextColor(0, 0, 0);
      const labelW = pdf.getTextDimensions(label).w;
      pdf.text(label, x, y);

      const valueX = x + labelW + 2;
      this.setFont();

      const statusGap = status ? 3 : 0;
      let statusW = 0;
      if (status) {
        try {
          this.setFont(0, "bold");
          statusW = Number(pdf.getTextWidth(status)) || 0;
        } catch (_) {
          statusW = 0;
        }
        this.setFont();
      }

      const valueMaxW = Math.max(20, rightEdge - valueX - (status ? statusW + statusGap : 0));
      const docLines = pdf.splitTextToSize(String(docNo || "").trim(), valueMaxW);
      pdf.setTextColor(0, 0, 0);
      if (docLines.length) {
        pdf.text(docLines, valueX, y, { lineHeightFactor: this.lineHeightFactor });
      }

      if (status) {
        this.setFont(0, "bold");
        pdf.setTextColor(200, 0, 0);
        pdf.text(status, rightEdge, y, { align: "right" });
        pdf.setTextColor(0, 0, 0);
        this.setFont();
      }

      rightY = y + Math.max(1, docLines.length) * lineHeight;
    };

    // ขวา: เรียงตามที่ขอ
    // แสดงสถานะเอกสารเป็นตัวหนังสือสีแดงที่มุมขวาบน (บรรทัดเดียวกับเลขที่เอกสาร ไม่เพิ่มแถวใหม่)
    // อิงจากค่าที่หน้าเว็บใช้: row.original.QtStatusName
    const docStatusName = String(qtTransaction?.QtStatusName || "").trim();
    // ถ้าเอกสารถูกอนุมัติแล้วและมีเลขใบเสนอราคา (QuoteNumber) ให้แสดงเลขนั้นแทน DocNumber
    const quoteNumber = String(qtTransaction?.QuoteNumber ?? this.data?.QuoteNumber ?? "").trim();
    renderRightTopRowWithStatus({
      docNo: quoteNumber || qtTransaction?.DocNumber || this.data?.DocNumber || "",
      statusText: docStatusName,
    });

    renderRightRow(
      "วันที่/ Date : ",
      qtTransaction?.DocDate
        ? moment(qtTransaction.DocDate, "YYYYMMDD").format("DD/MM/YYYY")
        : ""
    );
    renderRightRow(
      "Revise No : ",
      qtTransaction?.Revision !== undefined && qtTransaction?.Revision !== null
        ? String(qtTransaction.Revision)
        : ""
    );
    renderRightRow(
      "ยืนราคา/ Validity : ",
      (() => {
        const days = extractFirstInt(qtTransaction?.BillRefNo);
        if (!Number.isFinite(days)) return "";

        let out = `${days} วัน`;
        if (docDateMoment) {
          try {
            const until = docDateMoment.clone().add(days, "days");
            if (until.isValid()) out += ` ถึงวันที่ : ${until.format("DD/MM/YYYY")}`;
          } catch (_) {
            // ignore
          }
        }
        return out;
      })()
    );
    renderRightRow(
      "กำหนดส่งของ/ DeliveryDate : ",
      (() => {
        const raw = String(qtTransaction?.ReturnReasonCode ?? "").trim();
        const lower = raw.toLowerCase();
        if (!raw || raw === "-" || lower === "undefined" || lower === "null") return "N/A";

        const tokenHasDayWord = (s) => {
          const t = String(s ?? "").toLowerCase();
          return t.includes("วัน") || t.includes("day");
        };

        const isDateLike = (s) => {
          const t = String(s ?? "").trim();
          if (!t) return false;
          if (t.includes("/")) return true;
          // Date-like often has 2 hyphens (e.g., 02-04-68). Numeric range has 1.
          const hyphens = (t.match(/-/g) || []).length;
          if (hyphens >= 2) return true;
          return false;
        };

        const shouldAppendThaiDay = (s) => {
          const t = String(s ?? "").trim();
          if (!t) return false;
          if (tokenHasDayWord(t)) return false;
          if (isDateLike(t)) return false;
          // Numeric: 60
          if (/^\d+$/.test(t)) return true;
          // Numeric range: 60-90 (allow spaces around '-')
          if (/^\d+\s*-\s*\d+$/.test(t)) return true;
          return false;
        };

        // Support lists like "60,60-90".
        const parts = raw.split(",");
        if (parts.length <= 1) {
          return shouldAppendThaiDay(raw) ? `${raw} วัน` : raw;
        }

        const formatted = parts
          .map((p) => String(p ?? "").trim())
          .filter(Boolean)
          .map((p) => (shouldAppendThaiDay(p) ? `${p} วัน` : p));

        return formatted.length ? formatted.join(", ") : "N/A";
      })()
    );
    renderRightRow(
      "ชื่อพนักงาน/ Salesman : ",
      `${qtTransaction?.SalesmanName || ""}${qtTransaction?.SalesmanCode ? ` (${qtTransaction.SalesmanCode})` : ""}`.trim()
    );

    const rightHeight = rightY - rightStartY + 3;
    const boxHeight = Math.max(leftHeight, rightHeight) + 1;

    pdf.rect(formX, this.yAxis, midX - formX, boxHeight);
    pdf.rect(midX, this.yAxis, formX + formW - midX, boxHeight);

    this.yAxis += boxHeight + 5;
  }

  async renderInfoText() {
    const pdf = this.pdf;
    const { x: formX, w: formW } = this.getFormBox();

    const text =
      "บริษัทฯ มีความยินดีที่จะเสนอราคา ดังรายการต่อไปนี้\nWe are pleased to submit our quotation as follows";

    this.setFont();
    const maxWidth = formW;
    const lines = pdf.splitTextToSize(text, maxWidth);

    // Keep enough line spacing for Thai stacking marks.
    const lineHeightFactor = this.lineHeightFactor;
    pdf.text(lines, formX, this.yAxis, { lineHeightFactor });

    // advance yAxis based on rendered lines (jsPDF unit = mm)
    const fontSizePt = typeof pdf.getFontSize === "function" ? pdf.getFontSize() : this.baseFontSize;
    const ptToMm = 0.3527777778;
    const lineHeightMm = fontSizePt * ptToMm * lineHeightFactor;
    // Reduce gap before item table (make table sit closer to the info text)
    this.yAxis += Math.max(1, lines.length) * lineHeightMm;
  }

  // Backward compatibility (in case any old callers exist)
  async renderinfotext() {
    return this.renderInfoText();
  }
  async renderItemTable(items = [], startIndex = 0, { skipInfoText = false } = {}) {
    const { l, r } = this.pdfConfig.margin;

    if (!skipInfoText && startIndex === 0) {
      await this.renderInfoText();
    }

    const tableStartY = this.yAxis;

    const tableRows = this.buildItemTableRows(items, startIndex);

    // NOTE: Do not pad empty rows.
    // Let autoTable calculate actual height based on real rows
    // so sections below (summary + footer remarks table) won't be pushed/overlapped.

    const pageW = this.width;
    const { tableX: desiredTableX, tableW: desiredTableW } = this.getItemTableLayout(pageW);
    const opts = this.getItemTableAutoTableOptions({
      startY: tableStartY,
      tableRows,
      pageW,
    });
    this.pdf.autoTable(opts);

    // วาดเส้นล่างสุดของกรอบตาราง (ยังคงมีกรอบตาราง แต่ไม่ขีดเส้นใต้แต่ละรายการ)
    const tableEndY = this.pdf.lastAutoTable.finalY;
    // this.pdf.line(l, tableEndY, this.width - r, tableEndY); // No longer needed as didParseCell handles it

    // Store item table box so other sections can align width exactly.
    // In some autoTable versions, `lastAutoTable.table.width` may be missing; fall back to configured widths.
    try {
      const t = this.pdf.lastAutoTable;
      const actualX = Number(t?.table?.x);
      const actualW = Number(t?.table?.width);
      const columnsW = Array.isArray(t?.table?.columns)
        ? t.table.columns.reduce((sum, c) => sum + (Number(c?.width) || 0), 0)
        : NaN;
      this.itemTableBox = {
        x: Number.isFinite(actualX) ? actualX : desiredTableX,
        w: Number.isFinite(actualW)
          ? actualW
          : Number.isFinite(columnsW) && columnsW > 0
            ? columnsW
            : desiredTableW,
        y: tableStartY,
        finalY: tableEndY,
      };
    } catch (_) {
      this.itemTableBox = { x: desiredTableX, w: desiredTableW, y: tableStartY, finalY: tableEndY };
    }

    this.yAxis = tableEndY;
  }

  getProductRemarksFooterLines() {
    const items = this.data.QuotationTransactionItem || [];
    const allFooters = [];

    items.forEach((item) => {
      const footers = item.ProductRemarksFooter || [];
      if (footers.length > 0) allFooters.push(...footers);
    });

    if (allFooters.length === 0) return [];

    allFooters.sort((a, b) => (a.LineNo || 0) - (b.LineNo || 0));
    return allFooters
      .map((footer) => {
        const subject = footer.Subject || "";
        const remarks = footer.Remarks || "";
        const text = subject ? `${subject}: ${remarks}` : remarks;
        return String(text || "").trim();
      })
      .filter(Boolean);
  }

  getProductRemarksFooterRows() {
    const items = this.data.QuotationTransactionItem || [];
    const globalFooters = Array.isArray(this.data.ProductRemarksFooter)
      ? this.data.ProductRemarksFooter
      : [];

    const normalizeKeyLoose = (key) => String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const getLoose = (obj, key) => {
      if (!obj || typeof obj !== "object") return undefined;
      if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
      const target = normalizeKeyLoose(key);
      for (const k of Object.keys(obj)) {
        if (normalizeKeyLoose(k) === target) return obj[k];
      }
      return undefined;
    };

    const normalizeCode = (val) => String(val ?? "").trim();
    const normalizeCodeLoose = (val) =>
      String(val ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

    const getFooterProductCode = (footer) =>
      normalizeCode(
        getLoose(footer, "ProductCode") ||
        getLoose(footer, "productCode") ||
        getLoose(footer, "Productcode") ||
        getLoose(footer, "Product_Code") ||
        getLoose(footer, "Product_Code2") ||
        getLoose(footer, "ItemCode") ||
        getLoose(footer, "itemCode") ||
        getLoose(footer, "Itemcode") ||
        ""
      );

    const getFooterLineNo = (footer) => {
      const v =
        getLoose(footer, "LineNo") ??
        getLoose(footer, "Line_No") ??
        getLoose(footer, "Line") ??
        getLoose(footer, "Seq") ??
        getLoose(footer, "Sequence") ??
        0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const getFooterSubject = (footer) =>
      normalizeCode(
        getLoose(footer, "Subject") ??
        getLoose(footer, "subject") ??
        getLoose(footer, "Title") ??
        getLoose(footer, "title") ??
        ""
      );

    const getFooterStyle = (footer) =>
      normalizeCode(
        getLoose(footer, "Style") ??
        getLoose(footer, "style") ??
        ""
      );

    const getFooterColor = (footer) =>
      normalizeCode(
        getLoose(footer, "Color") ??
        getLoose(footer, "color") ??
        ""
      );

    const getFooterRemarks = (footer) =>
      normalizeCode(
        String(
          getLoose(footer, "Remarks") ??
          getLoose(footer, "remarks") ??
          getLoose(footer, "Remark") ??
          getLoose(footer, "remark") ??
          getLoose(footer, "Description") ??
          getLoose(footer, "description") ??
          ""
        ).replace(/\r\n?/g, "\n")
      );

    const footerRowKey = (footer, productCodeOverride) => {
      const pc = normalizeCode(productCodeOverride ?? getFooterProductCode(footer));
      const ln = getFooterLineNo(footer);
      const s = getFooterSubject(footer);
      const r = getFooterRemarks(footer);
      const st = getFooterStyle(footer);
      const c = getFooterColor(footer);
      return `${normalizeCodeLoose(pc)}|${ln}|${normalizeCodeLoose(s)}|${normalizeCodeLoose(r)}|${normalizeCodeLoose(st)}|${normalizeCodeLoose(c)}`;
    };

    const rows = [];
    const seenCodes = new Set();
    const usedFooterKeys = new Set();

    // ตาม requirement: map จาก QuotationTransactionItem.ProductCode แล้วเอาไปหาใน ProductRemarksFooter
    items.forEach((item) => {
      const productCode = normalizeCode(item?.ProductCode || item?.ItemCode || "");
      if (!productCode || seenCodes.has(productCode)) return;
      seenCodes.add(productCode);

      const productCodeLoose = normalizeCodeLoose(productCode);

      const footers = globalFooters.length
        ? globalFooters.filter((f) => {
          const fc = getFooterProductCode(f);
          if (!fc) return false;
          return normalizeCodeLoose(fc) === productCodeLoose;
        })
        : Array.isArray(item?.ProductRemarksFooter)
          ? item.ProductRemarksFooter
          : [];

      footers
        .slice()
        .sort((a, b) => getFooterLineNo(a) - getFooterLineNo(b))
        .forEach((f) => {
          const k = footerRowKey(f, productCode);
          if (k && usedFooterKeys.has(k)) return;
          if (k) usedFooterKeys.add(k);
          rows.push({
            productCode,
            lineNo: getFooterLineNo(f),
            subject: getFooterSubject(f),
            remarks: getFooterRemarks(f),
            style: getFooterStyle(f),
            color: getFooterColor(f),
          });
        });
    });

    const filtered = rows.filter((r) => r.subject || r.remarks);

    // Ensure we don't drop footer rows that have no/mismatched product codes.
    // Append any global footer rows that weren't matched above.
    if (globalFooters.length) {
      const extra = globalFooters
        .slice()
        .map((f) => {
          const pc = getFooterProductCode(f);
          const k = footerRowKey(f, pc);
          return { f, pc, k };
        })
        .filter(({ f, k }) => {
          if (!f) return false;
          if (k && usedFooterKeys.has(k)) return false;
          const s = getFooterSubject(f);
          const r = getFooterRemarks(f);
          return !!(s || r);
        })
        .sort((a, b) => {
          const ac = normalizeCode(a.pc);
          const bc = normalizeCode(b.pc);
          if (ac !== bc) return ac.localeCompare(bc);
          return getFooterLineNo(a.f) - getFooterLineNo(b.f);
        })
        .map(({ f, pc, k }) => {
          if (k) usedFooterKeys.add(k);
          return {
            productCode: pc,
            lineNo: getFooterLineNo(f),
            subject: getFooterSubject(f),
            remarks: getFooterRemarks(f),
            style: getFooterStyle(f),
            color: getFooterColor(f),
          };
        });

      if (extra.length) filtered.push(...extra);
    }

    // Fallback: If API returns ProductRemarksFooter but product codes don't match items,
    // show all footer rows so data is not lost.
    if (!filtered.length && globalFooters.length) {
      return globalFooters
        .slice()
        .sort((a, b) => {
          const ac = normalizeCode(getFooterProductCode(a));
          const bc = normalizeCode(getFooterProductCode(b));
          if (ac !== bc) return ac.localeCompare(bc);
          return getFooterLineNo(a) - getFooterLineNo(b);
        })
        .map((f) => ({
          productCode: getFooterProductCode(f),
          lineNo: getFooterLineNo(f),
          subject: getFooterSubject(f),
          remarks: getFooterRemarks(f),
          style: getFooterStyle(f),
          color: getFooterColor(f),
        }))
        .filter((r) => r.subject || r.remarks);
    }

    return filtered;
  }

  async renderSummaryAndRemarkSection() {
    const pdf = this.pdf;
    const { l, r } = this.pdfConfig.margin;
    // Start exactly at the end of item table so borders touch
    const startY = this.yAxis;

    // Align summary/remark/footer tables to the exact item table width
    const tableX = this.itemTableBox?.x ?? l;
    const tableW = this.itemTableBox?.w ?? this.width - l - r;
    const tableRightX = tableX + tableW;

    // Match item table border thickness
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);

    // Some APIs may return QuotationTransaction as an array.
    const qtTransactionRaw = this.data.QuotationTransaction;
    const qtTransaction = Array.isArray(qtTransactionRaw) ? qtTransactionRaw[0] : qtTransactionRaw;

    const parseNumber = (val, fallback = 0) => {
      if (val === null || val === undefined || val === "") return fallback;
      if (typeof val === "number") return Number.isFinite(val) ? val : fallback;

      const s0 = String(val).trim();
      if (!s0) return fallback;

      // Handle common DB/API formats:
      // - "1,234.00"
      // - "1,234.00 บาท" / "฿1,234.00"
      // - "(1,234.00)" => negative
      let s = s0;
      let isNegative = false;
      if (/^\(.*\)$/.test(s)) {
        isNegative = true;
        s = s.slice(1, -1);
      }

      // Keep digits, minus and dot only; remove commas and currency text.
      s = s.replace(/,/g, "").replace(/[^0-9.\-]/g, "");
      if (!s || s === "-" || s === "." || s === "-.") return fallback;

      const n = Number(s);
      if (!Number.isFinite(n)) return fallback;
      return isNegative ? -Math.abs(n) : n;
    };

    // IMPORTANT: Do not calculate totals.
    // Only read allowed columns from QuotationTransaction. No loose key guessing.
    const pickColumnNumber = (obj, keys, fallback = 0) => {
      if (!obj || typeof obj !== "object") return parseNumber(fallback, 0);
      for (const k of keys) {
        if (!k) continue;
        const raw = obj[k];
        if (raw === undefined || raw === null || raw === "") continue;
        const n = parseNumber(raw, NaN);
        if (Number.isFinite(n)) return n;
      }
      return parseNumber(fallback, 0);
    };

    // Align the left border of summary section to the left border of "หน่วยละ / Unit Price" column.
    // Item columns: 0 No, 1 Code, 2 Desc, 3 Qty, 4 Unit Price, 5 Discount, 6 Amount
    const colWidths = this.getItemTableColumnWidths();
    const unitPriceColStartOffset = colWidths
      .slice(0, 4)
      .reduce((sum, v) => sum + (Number(v) || 0), 0);

    const sumX = tableX + unitPriceColStartOffset;
    const sumWidth = tableRightX - sumX;
    const remWidth = Math.max(0, sumX - tableX);
    const rowH = 6.5;

    // Requirement (per API model):
    // - รวมเงิน = QuotationTransaction.Total_Sum
    // - VAT = QuotationTransaction.Total_Vat
    // - ยอดเงินสุทธิ = QuotationTransaction.Total_GrandTotal
    // IMPORTANT: Do not re-calculate totals and do not fall back to other columns
    // (e.g. Total_Total) because it can look like VAT is added again.
    const totalSum = pickColumnNumber(qtTransaction, ["Total_Sum", "Total_sum"], 0);
    const totalVat = pickColumnNumber(qtTransaction, ["Total_Vat", "Total_vat"], 0);
    const grandTotal = pickColumnNumber(
      qtTransaction,
      ["Total_GrandTotal", "Total_Grandtotal"],
      0
    );

    if (!this.__totalsLoggedOnce) {
      try {
        // Plain console.log (no groups) to ensure it shows in normal console output.
        // eslint-disable-next-line no-console
        console.log("[QuotationPdf] Totals debug", {
          DocNumber: qtTransaction?.DocNumber ?? this.data?.DocNumber,
          raw: {
            Total_Sum: qtTransaction?.Total_Sum,
            Total_sum: qtTransaction?.Total_sum,
            Total_Vat: qtTransaction?.Total_Vat,
            Total_vat: qtTransaction?.Total_vat,
            Total_GrandTotal: qtTransaction?.Total_GrandTotal,
            Total_Grandtotal: qtTransaction?.Total_Grandtotal,
            // for comparison only (NOT USED)
            Total_Total: qtTransaction?.Total_Total,
            Total_Discount: qtTransaction?.Total_Discount,
          },
          picked: {
            totalSum,
            totalVat,
            grandTotal,
          },
        });
      } catch (_) { }
      this.__totalsLoggedOnce = true;
    }
    const hasVat = Number(totalVat) > 0;

    const { translate, translator } = this.options || {};
    const t = (token, fallback) =>
      typeof translator === "function" && token !== undefined && token !== null
        ? translator(token, fallback)
        : fallback;

    const summaryData = [
      { label: t(translate?.ISAPDFQuototal, "รวมเป็นเงิน / Subtotal"), val: numberFormat(totalSum) },
      // แสดงแถว VAT เฉพาะเมื่อ totalVat > 0
      ...(totalVat > 0
        ? [
          {
            label: hasVat
              ? t(translate?.ISAPDFQuoVAT7, "จำนวนภาษีมูลค่าเพิ่ม / VAT 7%")
              : t(translate?.ISAPDFQuoVAT, "จำนวนภาษีมูลค่าเพิ่ม / VAT"),
            val: numberFormat(totalVat),
          },
        ]
        : []),
      { label: t(translate?.ISAPDFQuoGrandTotal, "จำนวนเงินรวมทั้งสิ้น / Net Amount"), val: numberFormat(grandTotal) },
    ];

    const totalRightHeight = summaryData.length * rowH;

    // Right: Summary grid
    // Draw outer box once to avoid double-thick lines from stacked rects.
    pdf.rect(sumX, startY, sumWidth, totalRightHeight);
    for (let i = 1; i < summaryData.length; i++) {
      const y = startY + i * rowH;
      pdf.line(sumX, y, sumX + sumWidth, y);
    }

    summaryData.forEach((row, idx) => {
      const y = startY + idx * rowH;
      const textY = y + 4.5;

      this.setFont(0, "bold");
      pdf.text(row.label, sumX + 2, textY);

      this.setFont();
      pdf.text(row.val, sumX + sumWidth - 2, textY, { align: "right" });
    });

    // Left: Remark + Thai words (align separator to the top border of the last summary row)
    // We want the split line to be exactly level with the top line of "ยอดเงินสุทธิ".
    const wordH = rowH;
    const remH = totalRightHeight - wordH;
    const wordY = startY + remH;

    // Draw outer box once and a single separator line to avoid misaligned/double lines.
    pdf.rect(tableX, startY, remWidth, totalRightHeight);
    pdf.line(tableX, wordY, tableX + remWidth, wordY);

    const remarkLabel = "หมายเหตุ/ Remark :";
    const docRemark = String(qtTransaction?.DocRemark || "").trim();
    const remarkText = docRemark;
    const remarkY = startY + 5;

    this.setFont(0, "bold");
    pdf.text(remarkLabel, tableX + 2, remarkY);

    // Place remark value after the label, but avoid overlap if label is too wide.
    const labelW = typeof pdf.getTextWidth === "function" ? pdf.getTextWidth(remarkLabel) : 20;
    const inlineX = tableX + 2 + labelW + 2;
    const inlineMaxW = Math.max(0, tableX + remWidth - 2 - inlineX);

    this.setFont();
    if (inlineMaxW >= 20) {
      const lines = pdf.splitTextToSize(remarkText || "", inlineMaxW);
      if (lines.length) pdf.text(lines, inlineX, remarkY);
    } else {
      // Fallback: render value on the next line inside the remark box.
      const nextLineY = remarkY + 5;
      const lines = pdf.splitTextToSize(remarkText || "", Math.max(0, remWidth - 4));
      if (lines.length) pdf.text(lines, tableX + 2, nextLineY);
    }

    // Thai words (bottom cell)
    this.setFont(0, "bold");
    const thaiWords = numberToThaiText(grandTotal);
    pdf.text(`(${thaiWords})`, tableX + remWidth / 2, wordY + wordH / 2 + 1.5, {
      align: "center",
    });

    // Item footer remarks table (ProductRemarksFooter) aligned to the bottom line of summary/remark section
    const footerTableY = startY + totalRightHeight;
    await this.renderItemFooterRemarksTable({
      x: tableX,
      y: footerTableY,
      w: tableW,
    });

    // IMPORTANT: renderItemFooterRemarksTable may paginate to new pages.
    // It already tracks the bottom Y per page; keep yAxis from the renderer.
    this.yAxis = (Number.isFinite(this.yAxis) ? this.yAxis : footerTableY) + 5;
  }

  async renderItemFooterRemarksTable({ x, y, w }) {
    const pdf = this.pdf;
    // IMPORTANT: Do not remove existing ProductRemarksFooter rows.
    // We render ExtraConditions first (seq 1), then ProductRemarksFooter (seq 2).
    // If height is limited, paginate to new pages so all data is visible.
    const extraRows = this.getExtraConditionsFooterRows();
    const productFooterRows = this.getProductRemarksFooterRows();

    const normalizeFooterText = (val) =>
      String(val ?? "")
        // Normalize all CR/LF combinations into LF so jsPDF can split/render reliably.
        .replace(/\r\n?/g, "\n")
        .trim();

    const parseRgb = (raw) => {
      if (raw === null || raw === undefined) return null;
      if (Array.isArray(raw) && raw.length >= 3) {
        const r = Number(raw[0]);
        const g = Number(raw[1]);
        const b = Number(raw[2]);
        if ([r, g, b].every((n) => Number.isFinite(n))) return [r, g, b];
        return null;
      }

      const s = String(raw).trim();
      if (!s) return null;
      // Accept formats like "255,0,0" or "255, 0, 0"
      const parts = s.split(",").map((p) => Number(String(p).trim()));
      if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) {
        return [parts[0], parts[1], parts[2]];
      }
      return null;
    };

    const parseStyleFlags = (raw) => {
      const s = String(raw ?? "")
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/[;|]/g, ",")
        .trim();
      // Accept: "U", "I", "UI", "U,I", "I,U"
      const tokens = s.includes(",") ? s.split(",").filter(Boolean) : s ? [s] : [];
      const merged = tokens.join("");
      return {
        underline: merged.includes("U"),
        italic: merged.includes("I"),
      };
    };

    const splitNumberedRemark = ({ subject, remarks, style, color }) => {
      const s = String(subject || "").trim();
      const raw = normalizeFooterText(remarks);
      if (s || !raw) return [{ subject: s, remarks: raw, style, color }];

      // If the DB stores multiple conditions in one field like:
      // "1. ... 2. ..." then split into separate rows for better readability.
      // Avoid matching numbers inside amounts like 60,000 (no dot).
      const re = /(^|[\n\t ])(\d{1,2})\.\s+/g;
      const matches = [];
      let m;
      // eslint-disable-next-line no-cond-assign
      while ((m = re.exec(raw)) !== null) {
        const lead = m[1] || "";
        const n = m[2];
        const start = (m.index || 0) + lead.length;
        matches.push({ n, start });
        // Prevent infinite loop for zero-width matches
        if (re.lastIndex === m.index) re.lastIndex += 1;
      }

      if (matches.length < 2) return [{ subject: s, remarks: raw }];

      const parts = [];
      for (let i = 0; i < matches.length; i += 1) {
        const { n, start } = matches[i];
        const end = i + 1 < matches.length ? matches[i + 1].start : raw.length;
        const chunk = raw.slice(start, end).trim();
        if (!chunk) continue;
        const prefixRe = new RegExp(`^${n}\\.\\s*`);
        const rest = chunk.replace(prefixRe, "").trim();
        parts.push({ subject: `${n}.`, remarks: rest || "", style, color });
      }

      return parts.length ? parts : [{ subject: s, remarks: raw, style, color }];
    };

    const productRows = (productFooterRows || [])
      .flatMap((r) =>
        splitNumberedRemark({
          subject: r?.subject,
          remarks: r?.remarks,
          style: r?.style,
          color: r?.color,
        })
      )
      .map((r) => ({
        subject: String(r?.subject || "").trim(),
        remarks: normalizeFooterText(r?.remarks),
        style: String(r?.style ?? "").trim(),
        color: r?.color,
      }))
      .filter((r) => r.subject || r.remarks);

    if (!this.__footerDebugLoggedOnce) {
      try {
        const extraRaw =
          (Array.isArray(this.data?.ExtraConditions) && this.data.ExtraConditions) ||
          (Array.isArray(this.data?.extraConditions) && this.data.extraConditions) ||
          [];
        const prfRaw =
          (Array.isArray(this.data?.ProductRemarksFooter) && this.data.ProductRemarksFooter) ||
          (Array.isArray(this.data?.productRemarksFooter) && this.data.productRemarksFooter) ||
          [];

        // eslint-disable-next-line no-console
        console.log("[QuotationPdf] Footer debug", {
          DocNumber: this.data?.DocNumber,
          rawCounts: {
            ExtraConditions: extraRaw.length,
            ProductRemarksFooter: prfRaw.length,
            Items: Array.isArray(this.data?.QuotationTransactionItem) ? this.data.QuotationTransactionItem.length : 0,
          },
          normalizedCounts: {
            extraRows: extraRows.length,
            productFooterRows: Array.isArray(productFooterRows) ? productFooterRows.length : 0,
            productRows: productRows.length,
          },
          samples: {
            extraRows: extraRows.slice(0, 5),
            productFooterRows: (productFooterRows || []).slice(0, 5),
            productRows: productRows.slice(0, 5).map((r) => ({
              subject: r.subject,
              remarksLen: String(r.remarks || "").length,
              remarksPreview: String(r.remarks || "").slice(0, 120),
            })),
          },
        });
      } catch (_) { }
      this.__footerDebugLoggedOnce = true;
    }

    const headerRow = this.getRemarkFooterHeaderRow();

    let rows = [];
    if (extraRows.length) rows = rows.concat(extraRows);
    if (productRows.length) rows = rows.concat(productRows);

    // Always show the header line first (subject only + underline).
    if (headerRow?.subject) {
      const headerKey = String(headerRow.subject).replace(/\s+/g, "").trim();
      const firstKey = String(rows?.[0]?.subject || "").replace(/\s+/g, "").trim();
      if (!firstKey || firstKey !== headerKey) {
        rows.unshift({ ...headerRow });
      }
    }

    // Requirement: at least the table/grid should be visible even when there is no data.
    // Keep a blank row after the header so the grid is visible.
    if (!rows.length) rows = [{ subject: "", remarks: "", style: "", color: "" }];
    if (rows.length === 1 && rows[0]?.subject && !rows[0]?.remarks) {
      rows.push({ subject: "", remarks: "", style: "", color: "" });
    }

    // Footer image is rendered ONLY on the very last page.
    // We still must reserve its space on the final footer-remarks page to prevent overlap.
    const reservedBottomNoFooter = this.getReservedBottom({ includeFooterImage: false });
    const reservedBottomWithFooter = this.getReservedBottom({ includeFooterImage: true });

    const borderW = 0.2;

    // Use the same fit logic as the measurement helper, but keep full geometry for drawing.
    const ptToMm = 0.3527777778;
    const basePt = this.baseFontSize;
    // IMPORTANT: Keep footer remark font size consistent across pages.
    // If it doesn't fit, paginate (move rows) instead of shrinking text.
    const style = { fontSizePt: Math.max(7, basePt - 1), lineHeightFactor: this.lineHeightFactor, padX: 2, padY: 2 };

    const computeFit = ({ fontSizePt, lineHeightFactor, padX, padY, rowsToMeasure, maxHeight }) => {
      const effectiveRows = rowsToMeasure.length ? rowsToMeasure : [{ subject: "", remarks: "" }];

      pdf.setFontSize(fontSizePt);
      const lineHeightMm = fontSizePt * ptToMm * lineHeightFactor;

      const gap = 3;
      pdf.setFont(this.fontName, this.resolveFontStyle("bold", pdf));
      const maxSubjectTextW = effectiveRows.reduce((maxW, r) => {
        const t = String(r?.subject || "");
        return Math.max(maxW, pdf.getTextWidth(t));
      }, 0);

      const minSubjectColW = 28;
      const maxSubjectColW = Math.max(minSubjectColW, Math.min(55, w * 0.45));
      const subjectColW = Math.min(maxSubjectTextW + 2, maxSubjectColW);
      const subjectColWFinal = Math.max(minSubjectColW, subjectColW);

      const subjectX = x + padX;
      const remarksX = subjectX + subjectColWFinal + gap;
      const remarksW = Math.max(0, x + w - padX - remarksX);

      const measureRowHeight = (subject, remarks) => {
        const subjectText = String(subject || "");
        const subjectLines = pdf.splitTextToSize(subjectText, subjectColWFinal);

        pdf.setFont(this.fontName, this.resolveFontStyle("normal", pdf));
        const remarksText = normalizeFooterText(remarks);
        const remarkLines = remarksText ? pdf.splitTextToSize(remarksText, remarksW) : [""];

        const lines = Math.max(Math.max(1, subjectLines.length), Math.max(1, remarkLines.length));
        // Keep rows tight; avoid blank-line-like gaps when remarks are split into multiple numbered subjects.
        return lines * lineHeightMm + padY * 0.4;
      };

      let totalH = padY;
      let lastRowIndex = -1;
      for (let i = 0; i < effectiveRows.length; i++) {
        const h = measureRowHeight(effectiveRows[i].subject, effectiveRows[i].remarks);
        if (totalH + h + padY > maxHeight) break;
        totalH += h;
        lastRowIndex = i;
      }

      return {
        fontSizePt,
        lineHeightMm,
        padX,
        padY,
        totalH,
        lastRowIndex,
        measureRowHeight,
        subjectColW: subjectColWFinal,
        subjectX,
        remarksX,
        remarksW,
      };
    };

    const previewChunk = ({ rowsToMeasure, maxHeight }) => {
      const effectiveRows = rowsToMeasure.length ? rowsToMeasure : [{ subject: "", remarks: "" }];

      const fit = computeFit({
        ...style,
        rowsToMeasure: effectiveRows,
        maxHeight,
      });

      if (fit.lastRowIndex < 0) return { renderedCount: 0, usedHeight: 0, fit: null };
      return {
        renderedCount: fit.lastRowIndex + 1,
        usedHeight: fit.totalH + fit.padY,
        fit,
      };
    };

    const drawChunk = ({ rowsToRender, startY, maxHeight }) => {
      const effectiveRows = rowsToRender.length ? rowsToRender : [{ subject: "", remarks: "", style: "", color: "" }];

      const fit = computeFit({
        ...style,
        rowsToMeasure: effectiveRows,
        maxHeight,
      });

      if (fit.lastRowIndex < 0) return { renderedCount: 0, usedHeight: 0 };

      // Outer box only (no internal row separators)
      pdf.setLineWidth(borderW);
      pdf.rect(x, startY, w, fit.totalH + fit.padY);

      let cursorY = startY + fit.padY;
      for (let i = 0; i <= fit.lastRowIndex; i++) {
        const { subject, remarks, style: styleRaw, color: colorRaw } = effectiveRows[i];
        const rowH = fit.measureRowHeight(subject, remarks);
        const textY = cursorY + fit.padY + fit.lineHeightMm;

        const subjectText = String(subject || "");
        const flags = parseStyleFlags(styleRaw);
        const rgb = parseRgb(colorRaw);
        const rowColor = rgb || [0, 0, 0];

        // Subject
        const subjectFontStyle = flags.italic ? "bolditalic" : "bold";
        pdf.setFont(this.fontName, this.resolveFontStyle(subjectFontStyle, pdf));
        pdf.setFontSize(fit.fontSizePt);
        const subjectLines = pdf.splitTextToSize(subjectText, fit.subjectColW);

        // Apply row color + allow underline for subject.
        pdf.setTextColor(rowColor[0], rowColor[1], rowColor[2]);
        pdf.setDrawColor(rowColor[0], rowColor[1], rowColor[2]);

        const underlineOffsetMm = Math.max(0.4, fit.fontSizePt * ptToMm * 0.12);
        for (let j = 0; j < subjectLines.length; j += 1) {
          const line = String(subjectLines[j] ?? "");
          const lineY = textY + j * fit.lineHeightMm;
          if (line) pdf.text(line, fit.subjectX, lineY);
          if (flags.underline && line) {
            const lineW = typeof pdf.getTextWidth === "function" ? pdf.getTextWidth(line) : 0;
            if (Number.isFinite(lineW) && lineW > 0) {
              pdf.setLineWidth(0.15);
              pdf.line(fit.subjectX, lineY + underlineOffsetMm, fit.subjectX + lineW, lineY + underlineOffsetMm);
              pdf.setLineWidth(borderW);
            }
          }
        }

        // Reset text color for the rest of the row.
        pdf.setTextColor(0, 0, 0);
        pdf.setDrawColor(0, 0, 0);

        // Remarks
        const remarkFontStyle = flags.italic ? "italic" : "normal";
        pdf.setFont(this.fontName, this.resolveFontStyle(remarkFontStyle, pdf));
        const remarksText = normalizeFooterText(remarks);
        if (remarksText) {
          // Use row color if provided.
          pdf.setTextColor(rowColor[0], rowColor[1], rowColor[2]);
          const remarkLines = pdf.splitTextToSize(remarksText, fit.remarksW);
          pdf.text(remarkLines, fit.remarksX, textY);
          pdf.setTextColor(0, 0, 0);
        }

        cursorY += rowH;
      }

      return { renderedCount: fit.lastRowIndex + 1, usedHeight: fit.totalH + fit.padY };
    };

    // Paginate: render as many rows as possible on the current page, then continue on new pages.
    let remaining = rows.slice();
    let pageCount = 1;
    let renderedTotal = 0;
    let currentY = y;

    while (remaining.length) {
      const isAtTopAfterHeaderInfo = Math.abs(Number(currentY) - Number(this.yAxis)) < 0.01;
      const startsWithHeaderRow = this.isRemarkFooterHeaderRow(remaining[0]);

      // Reserve footer image space ONLY on the final page (where remaining rows finish).
      // If remaining rows would finish exactly at the bottom without footer.png,
      // force the tail (typically the last row) to spill to a new page.
      const maxHeightWithFooter = Math.max(0, (this.height - reservedBottomWithFooter) - currentY);
      const maxHeightNoFooter = Math.max(0, (this.height - reservedBottomNoFooter) - currentY);

      const previewWithFooter = maxHeightWithFooter > 0
        ? previewChunk({ rowsToMeasure: remaining, maxHeight: maxHeightWithFooter })
        : { renderedCount: 0, usedHeight: 0, fit: null };
      const previewNoFooter = maxHeightNoFooter > 0
        ? previewChunk({ rowsToMeasure: remaining, maxHeight: maxHeightNoFooter })
        : { renderedCount: 0, usedHeight: 0, fit: null };

      // Decision rules:
      // 1) If everything fits WITH footer space, use that (no overlap possible).
      // 2) If everything fits WITHOUT footer space but NOT with it, we MUST spill to a new page
      //    so footer.png can be placed safely on the final page.
      //    => render only what fits WITH footer space.
      // 3) Otherwise, use no-footer space to maximize content on intermediate pages.
      let useFooterReserved = false;
      if (previewWithFooter.renderedCount >= remaining.length && previewWithFooter.renderedCount > 0) {
        useFooterReserved = true;
      } else if (
        previewNoFooter.renderedCount >= remaining.length &&
        previewNoFooter.renderedCount > 0 &&
        previewWithFooter.renderedCount > 0
      ) {
        useFooterReserved = true;
      }

      // IMPORTANT: Keep header with at least one detail row.
      // If we are not at the top of a fresh page and only the header would fit,
      // move the whole footer block to a new page.
      const previewChosen = useFooterReserved ? previewWithFooter : previewNoFooter;
      if (startsWithHeaderRow && !isAtTopAfterHeaderInfo) {
        const minRequiredRows = 2;
        if (previewChosen.renderedCount > 0 && previewChosen.renderedCount < minRequiredRows) {
          this.pdf.addPage();
          this.yAxis = this.pdfConfig.margin.t;
          this.width = this.pdf.internal.pageSize.width;
          this.height = this.pdf.internal.pageSize.height;
          await this.renderHeader();
          if (this.showInfoSection) await this.renderInfoSection();
          currentY = this.yAxis;
          pageCount += 1;
          continue;
        }
      }

      // Edge case: footer-reserved space can't fit even one row, but no-footer can.
      // Move all remaining rows to a fresh page (where footer space will fit) rather than overlapping.
      if (
        !useFooterReserved &&
        previewNoFooter.renderedCount >= remaining.length &&
        previewNoFooter.renderedCount > 0 &&
        previewWithFooter.renderedCount === 0
      ) {
        this.pdf.addPage();
        this.yAxis = this.pdfConfig.margin.t;
        this.width = this.pdf.internal.pageSize.width;
        this.height = this.pdf.internal.pageSize.height;
        await this.renderHeader();
        if (this.showInfoSection) await this.renderInfoSection();
        currentY = this.yAxis;
        pageCount += 1;
        continue;
      }

      const maxHeight = useFooterReserved ? maxHeightWithFooter : maxHeightNoFooter;

      if (maxHeight <= 0) {
        // Need a new page.
        this.pdf.addPage();
        this.yAxis = this.pdfConfig.margin.t;
        this.width = this.pdf.internal.pageSize.width;
        this.height = this.pdf.internal.pageSize.height;
        await this.renderHeader();
        if (this.showInfoSection) await this.renderInfoSection();
        currentY = this.yAxis;
        pageCount += 1;
        continue;
      }

      const chunk = drawChunk({ rowsToRender: remaining, startY: currentY, maxHeight });
      if (!chunk.renderedCount) {
        // If nothing fits even with smallest font, continue on a fresh page.
        this.pdf.addPage();
        this.yAxis = this.pdfConfig.margin.t;
        this.width = this.pdf.internal.pageSize.width;
        this.height = this.pdf.internal.pageSize.height;
        await this.renderHeader();
        if (this.showInfoSection) await this.renderInfoSection();
        currentY = this.yAxis;
        pageCount += 1;
        continue;
      }

      renderedTotal += chunk.renderedCount;
      remaining = remaining.slice(chunk.renderedCount);
      this.yAxis = currentY + chunk.usedHeight;

      // Track bottom of the footer remarks table on this page.
      this.markFooterRemarksBottomY(this.yAxis, pdf);

      if (remaining.length) {
        // Continue on a new page (no footer image on intermediate pages).
        this.pdf.addPage();
        this.yAxis = this.pdfConfig.margin.t;
        this.width = this.pdf.internal.pageSize.width;
        this.height = this.pdf.internal.pageSize.height;
        await this.renderHeader();
        if (this.showInfoSection) await this.renderInfoSection();
        currentY = this.yAxis;
        pageCount += 1;
      }
    }

    if (pageCount > 1) {
      try {
        // eslint-disable-next-line no-console
        console.log("[QuotationPdf] Footer paginated", {
          DocNumber: this.data?.DocNumber,
          pages: pageCount,
          rows: rows.length,
          rendered: renderedTotal,
        });
      } catch (_) { }
    }

    return Math.max(0, this.yAxis - y);
  }

  // Backward compatibility for any existing callers
  async renderitemfooterremarks() {
    return this.renderItemFooterRemarksTable({
      x: this.pdfConfig.margin.l,
      y: this.yAxis,
      w: this.width - this.pdfConfig.margin.l - this.pdfConfig.margin.r,
    });
  }

  //   async renderSignatureSection() {
  //     const pdf = this.pdf;
  //     const { l, r } = this.pdfConfig.margin;
  //     const sigW = (this.width - l - r - 10) / 3;
  //     const sigH = 25;

  //     const titles = ["ผู้เสนอราคา", "ผู้ตรวจสอบ", "ผู้อนุมัติ"];

  //     for (let i = 0; i < 3; i++) {
  //       const x = l + i * (sigW + 5);
  //       pdf.rect(x, this.yAxis, sigW, sigH);

  //       this.setFont(-1);
  //       pdf.text(titles[i], x + sigW / 2, this.yAxis + 5, { align: "center" });

  //       pdf.text(
  //         "(..................................................)",
  //         x + sigW / 2,
  //         this.yAxis + 18,
  //         { align: "center" }
  //       );

  //       pdf.text(
  //         "วันที่ ........./........./.........",
  //         x + sigW / 2,
  //         this.yAxis + 23,
  //         { align: "center" }
  //       );
  //     }

  //     this.yAxis += sigH + 5;
  //   }
}

// ---------- THAI NUMBER ----------
function numberToThaiText(num) {
  if (num === null || num === undefined || num === "") return "";
  const numeric = Number(num);
  if (!Number.isFinite(numeric)) return "";

  const fixed = numeric.toFixed(2);
  const [intPartStr, decPartStr] = fixed.split(".");

  const digitWords = [
    "ศูนย์",
    "หนึ่ง",
    "สอง",
    "สาม",
    "สี่",
    "ห้า",
    "หก",
    "เจ็ด",
    "แปด",
    "เก้า",
  ];

  const convertChunk = (chunkNumber) => {
    // chunkNumber: 0..999999
    const chunk = Number(chunkNumber || 0);
    if (!Number.isFinite(chunk) || chunk <= 0) return "";

    const units = [
      { value: 100000, label: "แสน" },
      { value: 10000, label: "หมื่น" },
      { value: 1000, label: "พัน" },
      { value: 100, label: "ร้อย" },
      { value: 10, label: "สิบ" },
      { value: 1, label: "" },
    ];

    let remaining = chunk;
    let out = "";
    let hasHigher = false;

    for (const u of units) {
      const digit = Math.floor(remaining / u.value);
      remaining = remaining % u.value;

      if (digit === 0) continue;

      if (u.value === 10) {
        if (digit === 1) out += "สิบ";
        else if (digit === 2) out += "ยี่สิบ";
        else out += `${digitWords[digit]}สิบ`;
        hasHigher = true;
        continue;
      }

      if (u.value === 1) {
        if (digit === 1 && hasHigher) out += "เอ็ด";
        else out += digitWords[digit];
        continue;
      }

      out += `${digitWords[digit]}${u.label}`;
      hasHigher = true;
    }

    return out;
  };

  const convertMillionsFromString = (intStr) => {
    const s = String(intStr || "0").replace(/^0+(?=\d)/, "");
    if (s === "0") return "ศูนย์";

    const groups = [];
    for (let i = s.length; i > 0; i -= 6) {
      groups.unshift(s.slice(Math.max(0, i - 6), i));
    }

    let startIndex = 0;
    while (startIndex < groups.length && Number(groups[startIndex]) === 0) {
      startIndex += 1;
    }
    if (startIndex >= groups.length) return "ศูนย์";

    let out = "";
    for (let i = startIndex; i < groups.length; i++) {
      const value = Number(groups[i]);
      const text = convertChunk(value);
      if (text) out += text;
      if (i < groups.length - 1) out += "ล้าน";
    }
    return out || "ศูนย์";
  };

  const bahtText = `${convertMillionsFromString(intPartStr)}บาท`;

  if (decPartStr === "00") return `${bahtText}ถ้วน`;

  const satang = Number(decPartStr);
  const satangText = convertChunk(satang) || "ศูนย์";
  return `${bahtText}${satangText}สตางค์`;
}
